import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useEnviarAprobacion } from "../../../aprobaciones/enviarAprobacion";
import { restante } from "../../../acuerdos/cuentaRegresiva";
import { Modal, OverlayTrigger, Tooltip } from "react-bootstrap";

import HttpManager from "../../../../services/HttpManager";
import { hoyCdmx } from "../../../../utils/horaMexico";
import useEsTelefono from "../../../../utils/esTelefono";
import { abrirArchivo } from "../../../enrollment/visorArchivo";
import { cursoCorto } from "../../../../utils/cursoCorto";
import { horarioBonito } from "../../../../utils/horarioBonito";
import R from "./strings";
import { useUnaALaVez } from "../../../../utils/unaALaVez";
import AplicarPromocion, { PromoApp, PromoOption, PromoDraft, promoPending, chargeOf, initialGiftKey } from "./aplicarPromocion";
import BandaEnCamino from "../../../promos/bandaEnCamino";
import { EnCamino } from "../../../promos/tipos";
import { dineroCorto } from "../../../../utils/dineroCorto";
// Perezoso: la Limpieza importa serverError de este archivo (así no hay vuelta de importaciones al cargar).
const LimpiezaDelAlumno = React.lazy(() => import("../../../promos/limpiezaDelAlumno"));

/*
	"Registrar pago" (docs/ESPEC_COBROS_UNIFICADO.md, Etapa 1): el MISMO modal en Pagos por Procesar (desde el
	ticket, con el comprobante a la izquierda), en Pagos › Nuevo pago (en blanco) y en Finanzas del alumno (con el
	alumno puesto). Reglas que la pantalla solo ayuda a cumplir (el servidor las vuelve a validar todas):
	- Caja decide a qué va cada peso: "Por asignar" en oro y el botón apagado hasta que Recibido − Aplicado = 0.
	- Lo sugerido solo marca lo que el monto cubre COMPLETO, vencido primero; el sobrante lo asigna la cajera.
	- Cada renglón con su "Se aplica" (abono en cualquier renglón); en $0 = saldado.
	- Descuentos: promo del catálogo (sobre precio de lista) o especial pedido a Supervisión de Caja.
*/

export interface RegisterStudent { id: number; name?: string; firstName?: string; lastName?: string; identityCode?: string | null; }

export interface RegisterPaymentModalProps {
	campusId: number;
	currencySymbol: string;
	/** Finanzas del alumno: ya viene puesto. */
	initialStudent?: RegisterStudent | null;
	/** Desde un ticket de Pagos por Procesar: el detalle del ticket (prellenado, comprobantes, borrador). */
	ticket?: any | null;
	onClose: () => void;
	/** Al terminar (después de enseñar el resultado): el detalle del pago registrado. */
	onSuccess: (receipt: any) => void;
}

interface Promo { id: number; campusId: number; code: string; name: string; group: string; appliesTo: string; calcType: string; value: number; rules: any; conditions: string; validTo: string | null;
	/** Promos flexibles: fin de la cuenta regresiva (ISO con zona del plantel), cupo y si está agotada. */
	countdownToIso?: string | null; quota?: any; exhausted?: boolean;
	/** Promos de adelanto (ESPEC_PROMOS_ADELANTO): solo se aplican con «Aplicar Promoción», nunca desde el selector del renglón. */
	adelanto?: boolean; adelantoKind?: string | null;
	/** Diferencia reloj del servidor − reloj del teléfono al cargar (ms): la cuenta regresiva se mide con la hora del servidor. */
	serverNowIso?: string | null; offsetMs?: number; }
interface DReq { id: number; statusId: number; status: string; calcType: string; value: number; listAmount: number; discountAmount: number; reason: string; decisionNotes: string | null; rowKey: string; }
interface Row {
	key: string; kind: "tuition" | "article"; id: number; campusId: number; campusName: string; statusId: number;
	listAmount: number; subTotal: number; paidAmount: number; pending: number; currentDiscount: number;
	invoiceId: number | null; overdue: boolean; canDiscount: boolean; enrollmentId: number | null; typeId: number | null;
	monthIndex: number | null; monthsTotal: number | null; courseName: string; levelName: string; scheduleName: string;
	courseId?: number | null; levelId?: number | null; periodId?: number | null;
	periodStart: string | null; dueDate: string | null; isTicket: boolean; title: string; discountRequest: DReq | null;
	/** Recargo automático (Etapa 2): el recargo va dentro del renglón de su mensualidad (parentKey / surchargeKey). */
	surcharge: any | null; parentKey: string | null; surchargeKey: string | null; discountBlocked: string | null;
	/** Acuerdo comercial (Etapa 3): el precio ya lo puso el motor; agreementLock = la matrícula tiene acuerdo (no se combina otra promo). */
	agreement?: { id: number; name: string; status: string; state: string; listAmount: number; target: number; applied: string; note: string | null; conditionMet: boolean; conditionText: string | null } | null;
	agreementLock?: string | null;
	/** Mes regalado pendiente de pago adelantado: las mensualidades ("t123") que tienen que pagarse completas (de una vez) para que quede en $0. */
	conditionKeys?: string[] | null;
}
interface Line { on: boolean; apply: string; touched: boolean; promotionId: number | null; drId: number | null; note: string; }
/** A2': lo que falta adelantar por acuerdo pendiente (lista × N − lo ya pagado) y qué meses quedan en $0 al registrarlo. */
interface Advance { agreementId: number; name: string; type: string; text: string | null; amount: number; keys: string[]; pendingCount: number; gifts: { tuitionId: number; title: string; statusId: number; subTotal: number; studentId: number }[]; giftStudentId: number; ownerEnrollmentId: number;
	/** Promo de adelanto: solo con «Aplicar Promoción» (precargada con la matrícula que paga, la promo y el curso regalado del 2x1). */
	adelanto?: boolean; promotionId?: number | null; baseEnrollmentId?: number; giftEnrollmentId?: number | null; }
/** Multimes / 2x1 que ya no puede cumplirse (plazo vencido…): una supervisora puede «Aprobar de todos modos» (ESPEC_AUDITORIA_PREGUNTAS B). */
interface BlockedAdvance { agreementId: number; name: string; text: string; canApprove: boolean; }
/** Cambio de promoción (2026-10-27): la promo nueva reemplaza la de la matrícula; los meses pagados con descuento vuelven a lista. */
interface CambioMes { tuitionId: number; key: string; title: string; list: number; subTotal: number; paid: number; difference: number; pendingAfter: number; }
interface Cambio { k: string; studentId: number; enrollmentId: number; promotionId: number; promoName: string; text: string; blocked: string | null; months: CambioMes[]; repriced: CambioMes[]; difference: number; }
interface Person { student: RegisterStudent; campusNames: string[]; loading: boolean; rows: Row[]; warnings: string[]; promos: Promo[]; lines: Record<string, Line>; notes: string; canWaive?: boolean; advances: Advance[]; blocked?: BlockedAdvance[]; cleanupPending?: any[]; }

const DR_PENDIENTE = 1, DR_ACEPTADO = 2, DR_RECHAZADO = 3;
const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;
const money = (n: number, s: string) => dineroCorto(n, s);
const nameOf = (s: RegisterStudent) => (s.name || [s.firstName, s.lastName].filter(Boolean).join(" ")).trim();
const newKey = () => ((window as any).crypto && (window as any).crypto.randomUUID ? (window as any).crypto.randomUUID() : String(Date.now()) + "-" + Math.random().toString(16).slice(2));
const plain = (s: string) => (s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
const LB: React.CSSProperties = { position: "static", display: "block", margin: 0, whiteSpace: "normal", overflow: "visible" };
const fecha = (d: string | null) => d ? d.slice(8, 10) + "/" + d.slice(5, 7) + "/" + d.slice(0, 4) : "—";

/** Prefijo del 400 «la nota habla de una promoción y el pago no lleva ninguna» (CobrosService::PROMO_EN_NOTA). */
const PROMO_EN_NOTA = "[promo_en_nota]";

/** El mensaje del servidor tal cual (409 trae "error", 422/400 traen "errors"). */
export const serverError = (e: any): string => {
	const d = e?.response?.data;
	if (d) {
		if (d.errors && typeof d.errors === "object") {
			const parts: string[] = [];
			Object.keys(d.errors).forEach((k) => { const v = d.errors[k]; parts.push(Array.isArray(v) ? v[0] : String(v)); });
			if (parts.length) return parts.join(" ");
		}
		if (typeof d.error === "string" && d.error) return d.error;
		if (d.message) return String(d.message);
	}
	return e?.message ? String(e.message) : "Ocurrió un error inesperado.";
};

/** El mismo cálculo del servidor: SIEMPRE sobre el precio de lista; reemplaza al descuento que traiga el renglón. */
const discountFrom = (list: number, calc: string, value: number): number => {
	if (list <= 0.009) return 0;
	if (calc === "percent") return round2(list * Math.min(100, Math.max(0, value)) / 100);
	if (calc === "fixed_price") return round2(Math.max(0, list - value));
	return round2(Math.max(0, Math.min(list, value)));
};

/** Promos que pueden aplicar a este renglón (filtro rápido; el servidor revisa las reglas completas). */
const promosFor = (row: Row, promos: Promo[]): Promo[] => {
	/*
		Con acuerdo comercial no se combina otra promo del catálogo (Etapa 3). Desde 2026-10-27 sí se puede CAMBIAR: se ofrecen las de
		mensualidad como «Cambiar a …» y el cambio de promoción (abajo de la persona) reemplaza el acuerdo y reabre lo que tuvo descuento.
	*/
	if (!row.canDiscount) return [];
	const course = plain(row.courseName + " " + row.levelName);
	return promos.filter((p) => {
		if (p.campusId !== row.campusId) return false;
		if (p.adelanto) return false;
		if (row.agreementLock && (p.group !== "mensualidad" || p.name === row.agreementLock)) return false;
		const tipo = (p.appliesTo === "subscription" && row.kind === "tuition" && row.typeId === 0)
			|| (p.appliesTo === "monthly" && row.kind === "tuition" && row.typeId === 1) || (p.appliesTo === "article" && row.kind === "article");
		if (!tipo) return false;
		const r = p.rules || {};
		if (row.kind === "tuition" && Array.isArray(r.courses) && r.courses.length && !r.courses.some((k: string) => course.includes(k))) return false;
		// Promos flexibles: cursos y módulos reales; la que ya terminó (cuenta regresiva en cero) o está agotada no se ofrece.
		if (row.kind === "tuition" && ((r.courseIds || []).length || (r.levelIds || []).length)
			&& !(r.courseIds || []).includes(row.courseId) && !(r.levelIds || []).includes(row.levelId)) return false;
		if (row.kind === "tuition" && ((r.excludeCourseIds || []).includes(row.courseId) || (r.excludeLevelIds || []).includes(row.levelId))) return false;
		if (p.countdownToIso && Date.parse(p.countdownToIso) <= Date.now() + (p.offsetMs || 0)) return false;
		if (p.exhausted) return false;
		if (row.kind === "tuition" && Array.isArray(r.excludeCourses) && r.excludeCourses.some((k: string) => course.includes(k))) return false;
		if ((r.monthFrom || r.monthTo) && ((row.monthIndex || 0) < (r.monthFrom || 1) || (row.monthIndex || 0) > (r.monthTo || 999))) return false;
		if (r.monthsTotal && row.kind === "tuition" && row.monthsTotal !== r.monthsTotal) return false;
		if (r.freeLast && ((row.monthIndex || 0) <= (row.monthsTotal || 0) - r.freeLast)) return false;
		if (r.periodFrom && (!row.periodStart || row.periodStart < r.periodFrom)) return false;
		return true;
	});
};

const RegisterPaymentModal: React.FC<RegisterPaymentModalProps> = ({ campusId, currencySymbol, initialStudent, ticket, onClose, onSuccess }) => {
	const http = HttpManager.getInstance();
	const movil = useEsTelefono();
	const intentId: number | null = ticket ? Number(ticket.id) : null;
	const clientKey = useRef<string>(newKey());

	const [methods, setMethods] = useState<any[]>([]);
	const [busy, setBusy] = useState<boolean>(false);
	const [revisandoLimp, setRevisandoLimp] = useState<any | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	/** Aplicar Promoción: la persona cuyo panel está abierto y la promo escogida (una por pago). */
	const [promoOpen, setPromoOpen] = useState<number | null>(null);
	/** Cursos con todos sus meses a la vista (por persona y grupo); los demás enseñan los próximos y «Ver N Meses Más». */
	const [gruposAbiertos, setGruposAbiertos] = useState<Record<string, boolean>>({});
	/** La nota habla de una promo y el pago no lleva ninguna (2026-11-02): el servidor pregunta antes de guardar a precio de lista. */
	const [preguntaPromo, setPreguntaPromo] = useState<string | null>(null);
	const [sinPromo, setSinPromo] = useState<string | null>(null);
	const [promoApp, setPromoApp] = useState<PromoApp | null>(null);
	/** Precarga pedida en una persona: el aviso de un adelanto pendiente o el draft de un 2x1 cuyo ticket es de quien recibe (abre en quien paga). */
	const [promoPreload, setPromoPreload] = useState<{ studentId: number; draft: PromoDraft; key: string } | null>(null);
	/** La cajera quitó la promo: el borrador del ticket no se vuelve a precargar. */
	const [promoQuitada, setPromoQuitada] = useState<boolean>(false);
	/** Por persona: si tiene promos que ofrecer (false = sin botón «Aplicar Promoción» en su encabezado). */
	const [promoHas, setPromoHas] = useState<Record<number, boolean | null>>({});
	/** Por persona: sus promos En Camino / Por Cobrar vivas (aviso dorado arriba de su caja). */
	const [ecVivas, setEcVivas] = useState<Record<number, EnCamino[]>>({});
	/** Renglón que la promo escogida pide pagar (`payKeys`), sea completa o En Camino. */
	const promoKeyFor = (p: Person, row: Row): boolean => !!promoApp && promoApp.studentId === p.student.id && promoApp.option.payKeys.includes(row.key);
	/** Los renglones `payKeys` de la promo escogida van completos y no se tocan. En Camino NO: ahí el dinero entra como abono, en orden. */
	const lockedFor = (p: Person, row: Row): boolean => promoKeyFor(p, row) && !promoApp!.enCamino;
	/** Renglón apartado por la En Camino (meses a regalar o curso regalado): Caja no lo cobra. */
	const frozenFor = (p: Person, row: Row): boolean => !!promoApp && !!promoApp.enCamino && promoApp.studentId === p.student.id && (promoApp.enCamino.frozenKeys || []).includes(row.key);
	/**
		En Camino: lo del monto que de verdad va a los meses de la promo = lo recibido menos lo que se aplica a todo lo demás (sus recargos,
		otras personas, otros conceptos). Así lo cuenta el servidor (suma de los renglones `payKeys` de esa persona), no con todo lo recibido.
	*/
	const ecParaPromo = (): number => {
		if (!promoApp?.enCamino) return received;
		const otros = persons.reduce((acc, p) => acc + p.rows.reduce((s, row) => (p.student.id === promoApp.studentId && promoApp.option.payKeys.includes(row.key)
			? s : s + applyOf(p, row, p.lines[row.key] || ({} as Line))), 0), 0);
		return round2(received - otros - extraCambios);
	};
	/** En Camino: lo que va a sus meses llega a lo que falta, así que la promo se completa y reabre sus meses (la diferencia va en este mismo pago). */
	const enCaminoCompleta = (): boolean => {
		if (!promoApp?.enCamino) return true;
		const paraPromo = ecParaPromo();
		return paraPromo > 0.004 && paraPromo >= Number(promoApp.enCamino.remaining) - 0.005;
	};
	/** Recargos vencidos de los meses de la promo (`surchargeKeys`): van en el mismo pago, marcados al escoger la promo (la cajera puede desmarcar uno). */
	const promoRecargo = (p: Person, row: Row): boolean => !!promoApp && promoApp.studentId === p.student.id && (promoApp.option.surchargeKeys || []).includes(row.key);
	/** Lo de esos recargos que NO se cobra porque el pago es a tiempo y el servidor los anula solo (R4): se resta del `charge` de la lista. */
	const recargosATiempo = (p: Person, o: PromoOption): number => round2((o.surchargeKeys || []).reduce((acc, k) => {
		const row = p.rows.find((x) => x.key === k);
		return acc + (row && recargoAnulado(p, row) ? row.pending : 0);
	}, 0));
	/** Meses que el cambio de promoción reabre (lo dice el servidor): su diferencia va en ESTE pago. */
	const reopenedOf = (p: Person, row: Row): number | null => {
		if (!promoApp || promoApp.studentId !== p.student.id || !enCaminoCompleta()) return null;
		const m = (promoApp.option.reopenedMonths || []).find((x) => x.key === row.key);
		return m ? Number(m.difference) || 0 : null;
	};
	/** Los reabiertos que ya estaban pagados completos no vienen en `_owed`: no hay renglón en pantalla, van como renglón extra con apply = difference. */
	const reopenedLoose = (): { key: string; title: string; difference: number }[] => {
		if (!promoApp || !enCaminoCompleta()) return [];
		const rows = persons.find((x) => x.student.id === promoApp.studentId)?.rows || [];
		return (promoApp.option.reopenedMonths || []).filter((m) => !rows.some((r) => r.key === m.key));
	};
	/** Quitar la promo deja todo como antes de escogerla: sin panel abierto y sin volver a precargar el borrador del ticket. */
	const quitarPromo = () => {
		setPromoApp(null); setPromoOpen(null); setPromoQuitada(true);
	};
	const [result, setResult] = useState<any>(null);

	// Ticket con un depósito ya registrado: lo que reportó Ventas (total, referencia, fecha) es de todos los depósitos o del primero,
	// no del que falta. Se empieza en blanco y se llena con lo leído de la foto o a mano.
	const ticketParcial = !!ticket && (ticket.documents || []).some((d: any) => d.receiptId && !d.rejected);
	const [total, setTotal] = useState<string>(ticket?.amount && !ticketParcial ? String(ticket.amount) : "");
	const [methodId, setMethodId] = useState<number>(Number(ticket?.paymentMethodId || 0));
	const [accountId, setAccountId] = useState<number>(Number(ticket?.recipientAccountId || 0));
	const [paymentDate, setPaymentDate] = useState<string>(ticket?.paidAt && !ticketParcial ? String(ticket.paidAt).slice(0, 10) : hoyCdmx());
	const [reference, setReference] = useState<string>(ticketParcial ? "" : ticket?.reference || "");
	const [refCheck, setRefCheck] = useState<{ ok: boolean; repeated: boolean; message: string | null } | null>(null);
	const [payerName, setPayerName] = useState<string>("");
	const [payerPhone, setPayerPhone] = useState<string>("");
	const [notes, setNotes] = useState<string>(ticket ? (ticket.notes ? String(ticket.notes).split("\n")[0].slice(0, 500) : `Pago del ticket #${ticket.id}`) : "");
	const [files, setFiles] = useState<File[]>([]);

	const [persons, setPersons] = useState<Person[]>([]);
	// R3 (BAJO 9): ticket sin alumno en WAPP → la cajera marca de quién es el comprobante (por defecto, la primera persona del pago).
	const [owner, setOwner] = useState<number>(0);
	const [manual, setManual] = useState<boolean>(false);
	const [searchOpen, setSearchOpen] = useState<boolean>(!initialStudent && !ticket);
	const [query, setQuery] = useState<string>("");
	const [results, setResults] = useState<any[]>([]);
	const [searching, setSearching] = useState<boolean>(false);
	const [special, setSpecial] = useState<{ studentId: number; key: string; calcType: "percent" | "amount"; value: string; reason: string } | null>(null);
	// Eximir el recargo: Supervisión de Caja lo hace aquí mismo; la cajera lo sugiere (Cliq a la supervisora).
	// Esteban 2026-10-09: pagar tarde nunca quita el descuento, así que eximir no tiene nada que «devolver».
	const [waiver, setWaiver] = useState<{ studentId: number; key: string; surchargeId: number; mode: "waive" | "request"; calcType: "percent" | "amount"; value: string; reason: string; pending: number } | null>(null);
	const [preview, setPreview] = useState<{ url: string | null; type: string; name: string; index: number } | null>(null);
	// Lectura IA del comprobante del ticket (prellenado + avisos "Revisar"). Sin IA: campos vacíos, sin error.
	const [ai, setAi] = useState<{ loading: boolean; prefill: any; warnings: string[]; readings: any[]; deposits?: any[] } | null>(ticket ? { loading: true, prefill: null, warnings: [], readings: [] } : null);
	const aiApplied = useRef<boolean>(false);
	const pendingAccount = useRef<number>(0);
	/*
		Todo comprobante se lee (Esteban, 2026-11-02): la foto que Caja adjunta aquí, venga o no de un ticket, pasa por la misma lectura
		(prellenado, avisos, OXXO) y por la huella: un archivo que ya está en otro pago no se puede registrar. Solo en métodos que exigen
		comprobante (el voucher de la Terminal POS no). El servidor lo vuelve a revisar todo al registrar.
	*/
	const [adjuntos, setAdjuntos] = useState<{ loading: boolean; warnings: string[]; readings: any[]; duplicates: string[]; prefill: any } | null>(null);
	const adjuntosLeidos = useRef<string>("");

	const method = useMemo(() => methods.find((m: any) => Number(m.id) === methodId) || null, [methods, methodId]);
	const accounts: any[] = method?.accounts || [];
	/*
		Con promo de adelanto escogida y dinero que entra en persona (efectivo / Terminal POS) no hay comprobante con fecha que revisar:
		la fecha de pago es hoy y no se edita (el candado de la 3ra mensualidad no se salta con una fecha anterior).
		La cuenta dice si es presencial (`presencial`); si no lo trae, se reconoce por el nombre del método.
	*/
	const cuentaSel = accounts.find((a: any) => Number(a.id) === accountId);
	const esPresencial: boolean = cuentaSel && typeof cuentaSel.presencial === "boolean" ? cuentaSel.presencial
		: !cuentaSel && (method?.presencial === true || (accounts.length > 0 && accounts.every((a: any) => a.presencial === true))) ? true
			: /efectivo|terminal|\bpos\b|planilla/i.test(method?.name || "");
	const fechaFija = !!promoApp && esPresencial;
	useEffect(() => {
		if (fechaFija && paymentDate !== hoyCdmx()) setPaymentDate(hoyCdmx());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fechaFija, paymentDate]);
	const ticketDocs: any[] = ticket ? (ticket.documents && ticket.documents.length ? ticket.documents : (ticket.hasDocument ? [{ index: 0, name: ticket.documentName || "Comprobante" }] : [])).filter((d: any) => !d.rejected) : [];
	const draft = ticket?.draft || null;
	/*
		Dos borradores distintos en el ticket: el de Caja («Guardar Borrador»: total, método, personas, promo) y el que nace con una
		Solicitud de Promoción de la asesora (ESPEC_SOLICITUD_PROMO: solo `promoApplication`, lo lee el servidor y lo manda en
		_promoOptions como `draft`). El segundo no pisa el monto ni el método del ticket; solo hace que la promo se precargue.
	*/
	const cajaDraft: boolean = !!draft && (draft.total !== undefined || Array.isArray(draft.persons) || !!draft.promo);
	const esSolicitudPromo: boolean = !!ticket && (Number(ticket.sourceId) === 2 || (!!draft && !!draft.promoApplication && !draft.promo));
	/*
		Varios depósitos en un ticket (Esteban, 2026-10-27: "si hay diferentes deben capturarse varios pagos"): con 2+ fotos la cajera
		marca cuáles son de este pago; las demás quedan para el siguiente pago del mismo ticket. Las ya registradas no se ofrecen.
	*/
	const variosDepositos = ticketDocs.length >= 2;
	const fotosLibres: number[] = ticketDocs.filter((d: any) => !d.receiptId).map((d: any) => Number(d.index));
	const [fotos, setFotos] = useState<number[]>(() => {
		// Del borrador, solo las que siguen libres.
		const delBorrador = Array.isArray(ticket?.draft?.fotos) ? ticket.draft.fotos.map(Number).filter((k: number) => fotosLibres.includes(k)) : [];
		return delBorrador.length ? delBorrador : fotosLibres;
	});
	const fotosTocadas = useRef<boolean>(false);

	// ------------------------------------------------------------------ carga

	useEffect(() => {
		http.paymentService.registerMethods(campusId, !!ticket).then((r: any) => {
			const list = r.data || [];
			setMethods(list);
			// El método que pide el borrador o el ticket, si está permitido aquí; si no, el primero de la lista.
			const wanted = Number((draft && draft.methodId) || ticket?.paymentMethodId || 0);
			// Si ya hay un método válido elegido (por la lectura del comprobante o por la cajera), no se pisa.
			setMethodId((prev) => (prev && list.some((m: any) => Number(m.id) === prev)) ? prev
				: (list.some((m: any) => Number(m.id) === wanted) ? wanted : (list.length ? Number(list[0].id) : 0)));
		}).catch((e: any) => setError(serverError(e)));
		//Un ticket sin alumno en WAPP (el que mandó el alumno por /pagar y no se encontró) no lleva persona fantasma: pedía /_owed/0 y salía "Alumno no encontrado".
		const first: RegisterStudent | null = ticket ? (Number(ticket.studentId) > 0 ? { id: Number(ticket.studentId), name: ticket.studentName, identityCode: ticket.identityCode } : null) : (initialStudent || null);
		const people: RegisterStudent[] = [];
		if (first) people.push(first);
		// Personas que se matricularon juntas (Etapa 3): el mismo ticket las cubre a todas.
		(ticket?.companions || []).forEach((c: any) => { if (!people.some((x) => x.id === Number(c.studentId))) people.push({ id: Number(c.studentId), name: c.name }); });
		if (draft && Array.isArray(draft.persons)) {
			draft.persons.forEach((p: any) => { if (!people.some((x) => x.id === p.student?.id)) people.push(p.student); });
		}
		if (draft && cajaDraft) {
			setTotal(draft.total ?? ""); setMethodId(Number(draft.methodId || 0)); setAccountId(Number(draft.accountId || 0));
			setPaymentDate(draft.paymentDate || hoyCdmx()); setReference(draft.reference || ""); setPayerName(draft.payerName || "");
			setPayerPhone(draft.payerPhone || ""); setNotes(draft.notes || ""); setManual(true);
			setNotice(R.draftRestored(ticket.draftAt ? ticket.draftAt.replace("T", " ") : ""));
		}
		people.forEach((s) => addPerson(s, cajaDraft ? draft : null));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!method) return;
		if (pendingAccount.current && accounts.some((a: any) => Number(a.id) === pendingAccount.current)) {
			setAccountId(pendingAccount.current);
			pendingAccount.current = 0;
		} else if (!accounts.some((a: any) => Number(a.id) === accountId)) setAccountId(accounts.length === 1 ? Number(accounts[0].id) : 0);
		if (method.dateLocked) setPaymentDate(hoyCdmx());
		setRefCheck(null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [methodId, methods]);

	useEffect(() => {
		if (!ticket) return;
		http.paymentIntentsService.voucher(campusId, Number(ticket.id))
			.then((r: any) => setAi({ loading: false, prefill: r.data?.prefill || null, warnings: r.data?.warnings || [], readings: r.data?.readings || [], deposits: r.data?.deposits || [] }))
			.catch(() => setAi({ loading: false, prefill: null, warnings: [], readings: [] }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	useEffect(() => {
		const clave = files.map((f) => f.name + ":" + f.size + ":" + f.lastModified).join("|") + "#" + (method?.attachmentRequired ? 1 : 0);
		if (clave === adjuntosLeidos.current) return;
		adjuntosLeidos.current = clave;
		if (!files.length || !method?.attachmentRequired) { setAdjuntos(null); return; }
		let vivo = true;
		setAdjuntos({ loading: true, warnings: [], readings: [], duplicates: [], prefill: null });
		Promise.all(files.map((f) => http.paymentService.readVoucher(campusId, f).then((r: any) => r.data).catch(() => null))).then((rs: any[]) => {
			if (!vivo) return;
			const ok = rs.filter(Boolean);
			const p = (ok.find((x: any) => x.prefill) || {}).prefill || null;
			setAdjuntos({
				loading: false,
				warnings: Array.from(new Set(ok.flatMap((x: any) => (x.duplicate ? [] : x.warnings || [])))) as string[],
				readings: ok.flatMap((x: any) => x.readings || []),
				duplicates: ok.filter((x: any) => x.duplicate).map((x: any) => String(x.duplicate)),
				prefill: p
			});
			if (!p) return;
			// Llena solo lo vacío; la referencia de OXXO siempre es su ID de transacción.
			if (p.oxxoTransactionId) setReference(String(p.oxxoTransactionId));
			else if (p.reference) setReference((r) => (r.trim() ? r : String(p.reference)));
			if (p.amount) setTotal((t) => (t && Number(t) > 0 ? t : String(p.amount)));
			if (p.paymentDate) setPaymentDate((d) => (d === hoyCdmx() ? p.paymentDate : d));
			if (p.accountId && Number(p.paymentMethodId) === methodId && accounts.some((a: any) => Number(a.id) === Number(p.accountId))) {
				setAccountId((a) => (a ? a : Number(p.accountId)));
			}
		});
		return () => { vivo = false; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [files, method?.attachmentRequired]);
	/* Lo leído llena solo lo que el ticket o el borrador no traen; la cajera siempre puede corregir. */
	useEffect(() => {
		if (!ai || ai.loading || !ai.prefill || aiApplied.current || methods.length < 1 || draft) return;
		aiApplied.current = true;
		const p = ai.prefill;
		// Varios depósitos: lo de Ventas es el total de todos; manda lo leído de ESTE depósito, y quedan marcadas solo sus fotos.
		const porDeposito = variosDepositos && (ai.deposits || []).length > 1;
		if (porDeposito && Array.isArray(p.docIndexes) && !fotosTocadas.current) {
			const suyas = p.docIndexes.map(Number).filter((k: number) => fotosLibres.includes(k));
			if (suyas.length) setFotos(suyas);
		}
		if (porDeposito) {
			// Cada depósito con lo suyo: lo que la IA no leyó se queda en blanco, nunca con el total de Ventas.
			setTotal(p.amount ? String(p.amount) : "");
			setPaymentDate(p.paymentDate || hoyCdmx());
			setReference(p.reference || "");
		} else {
			if ((ticketParcial || !ticket.amount) && p.amount) setTotal(String(p.amount));
			if ((ticketParcial || !ticket.paidAt) && p.paymentDate) setPaymentDate(p.paymentDate);
			if ((ticketParcial || !ticket.reference) && p.reference) setReference(p.reference);
		}
		const okMethod = p.paymentMethodId && methods.some((m: any) => Number(m.id) === Number(p.paymentMethodId));
		if (!ticket.paymentMethodId && okMethod) {
			if (Number(p.paymentMethodId) === methodId) {
				if (!ticket.recipientAccountId && p.accountId && accounts.some((a: any) => Number(a.id) === Number(p.accountId))) setAccountId(Number(p.accountId));
			} else {
				if (!ticket.recipientAccountId && p.accountId) pendingAccount.current = Number(p.accountId);
				setMethodId(Number(p.paymentMethodId));
			}
		} else if (!ticket.recipientAccountId && p.accountId && accounts.some((a: any) => Number(a.id) === Number(p.accountId))) {
			setAccountId(Number(p.accountId));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ai, methods]);

	// Vista previa del comprobante del ticket, a la izquierda (nunca otra ventana).
	const showDoc = async (index: number) => {
		try {
			const r = await http.paymentIntentsService.downloadDocument(campusId, Number(ticket.id), index);
			const blob: Blob = r.data;
			if (preview?.url) URL.revokeObjectURL(preview.url);
			setPreview({ url: URL.createObjectURL(blob), type: blob.type || "", name: ticketDocs.find((d: any) => d.index === index)?.name || "Comprobante", index });
		} catch (e) {
			setPreview({ url: null, type: "", name: "", index });
		}
	};
	useEffect(() => {
		if (ticketDocs.length) showDoc(ticketDocs[0].index);
		return () => { if (preview?.url) URL.revokeObjectURL(preview.url); };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const added = useRef<Set<number>>(new Set());
	// Promos flexibles: cada 30 s se vuelve a pintar para que la promo que llegó a cero desaparezca sola de la lista.
	const [, setReloj] = useState<number>(0);
	useEffect(() => { const t = setInterval(() => setReloj((x) => x + 1), 30000); return () => clearInterval(t); }, []);
	// «Aprobar de todos modos» un multimes vencido (Supervisión de Caja; ESPEC_AUDITORIA_PREGUNTAS B). Un envío a la vez (doble toque).
	const [excepcion, setExcepcion] = useState<{ agreementId: number; reason: string } | null>(null);
	const [excBusy, setExcBusy] = useState<boolean>(false);
	const excEnviando = useRef<boolean>(false);
	const aprobarExcepcion = async (student: RegisterStudent, agreementId: number) => {
		if (!excepcion || excEnviando.current) return;
		excEnviando.current = true;
		setExcBusy(true);
		try {
			const r = await http.paymentService.approveAdvanceException(campusId, agreementId, excepcion.reason.trim(), "payments");
			setExcepcion(null);
			setError(null);
			setNotice(r.data?.text || R.exceptionDone);
			const keep = persons.find((x) => x.student.id === student.id)?.lines || null;
			const loaded = await loadPerson(student, keep);
			setPersons((prev) => prev.map((x) => x.student.id === student.id ? { ...x, ...loaded, loading: false } : x));
		} catch (e) {
			setError(serverError(e));
		} finally {
			excEnviando.current = false;
			setExcBusy(false);
		}
	};
	const loadPerson = async (student: RegisterStudent, keepLines?: Record<string, Line> | null) => {
		const r = await http.paymentService.registerOwed(campusId, student.id, intentId);
		const d = r.data;
		const lines: Record<string, Line> = {};
		(d.rows as Row[]).forEach((row) => {
			const prev = keepLines ? keepLines[row.key] : undefined;
			lines[row.key] = prev ? { ...prev } : { on: false, apply: "", touched: false, promotionId: null, drId: null, note: "" };
			if (!prev && row.discountRequest && row.discountRequest.statusId === DR_ACEPTADO) lines[row.key].drId = row.discountRequest.id;
		});
		return { rows: d.rows as Row[], warnings: d.warnings || [], promos: (d.promotions || []).map((x: Promo) => ({ ...x, offsetMs: x.serverNowIso ? Date.parse(x.serverNowIso) - Date.now() : 0 })), lines, canWaive: !!d.canWaive, advances: (d.advances || []) as Advance[], blocked: (d.blockedAdvances || []) as BlockedAdvance[], cleanupPending: (d.cleanupPending || []) as any[], student: { ...student, name: d.student?.name || nameOf(student), identityCode: d.student?.identityCode || student.identityCode }, campusNames: d.student?.campusNames || [] };
	};
	const addPerson = async (student: RegisterStudent, fromDraft?: any) => {
		if (!student || added.current.has(student.id)) return;
		added.current.add(student.id);
		setSearchOpen(false); setQuery(""); setResults([]);
		setPersons((prev) => [...prev, { student, campusNames: [], loading: true, rows: [], warnings: [], promos: [], lines: {}, notes: "", advances: [] }]);
		try {
			const saved = fromDraft && Array.isArray(fromDraft.persons) ? fromDraft.persons.find((p: any) => p.student?.id === student.id) : null;
			const loaded = await loadPerson(student, saved ? saved.lines : null);
			setPersons((prev) => prev.map((p) => p.student.id === student.id ? { ...p, ...loaded, loading: false, notes: saved?.notes || "" } : p));
		} catch (e) {
			setError(serverError(e));
			setPersons((prev) => prev.map((p) => p.student.id === student.id ? { ...p, loading: false } : p));
		}
	};
	const refreshPerson = async (studentId: number) => {
		const p = persons.find((x) => x.student.id === studentId);
		if (!p) return;
		try {
			const loaded = await loadPerson(p.student, p.lines);
			// Un especial que ya respondió: aceptado se usa; rechazado se quita.
			Object.keys(loaded.lines).forEach((k) => {
				const row = loaded.rows.find((x) => x.key === k);
				const l = loaded.lines[k];
				if (row?.discountRequest?.statusId === DR_ACEPTADO && !l.promotionId) l.drId = row.discountRequest.id;
				if (l.drId && (!row?.discountRequest || row.discountRequest.id !== l.drId || row.discountRequest.statusId === DR_RECHAZADO)) l.drId = null;
			});
			setPersons((prev) => prev.map((x) => x.student.id === studentId ? { ...x, ...loaded, notes: x.notes } : x));
		} catch (e) {
			setError(serverError(e));
		}
	};
	const removePerson = (id: number) => { if (promoApp?.studentId === id) quitarPromo(); if (promoOpen === id) setPromoOpen(null); if (promoPreload?.studentId === id) setPromoPreload(null); added.current.delete(id); setPersons((prev) => prev.filter((p) => p.student.id !== id)); };

	const timer = useRef<any>(null);
	const search = (q: string) => {
		setQuery(q);
		clearTimeout(timer.current);
		if (q.trim().length < 2) { setResults([]); return; }
		timer.current = setTimeout(async () => {
			try {
				setSearching(true);
				const r = await http.paymentService.registerPeople(campusId, q.trim());
				setResults(r.data || []);
			} catch (e) {
				setError(serverError(e));
			} finally {
				setSearching(false);
			}
		}, 300);
	};

	// ------------------------------------------------------------------ montos

	const reqOf = (row: Row, l: Line): DReq | null => (l.drId && row.discountRequest && row.discountRequest.id === l.drId ? row.discountRequest : null);
	const discountOf = (p: Person, row: Row, l: Line): number => {
		if (l.promotionId) {
			const promo = p.promos.find((x) => x.id === l.promotionId);
			return promo ? discountFrom(row.listAmount, promo.calcType, promo.value) : 0;
		}
		// Un renglón de la promo de adelanto va a precio de lista: el descuento especial aceptado no se cuela (vuelve si se quita la promo).
		const dr = promoKeyFor(p, row) ? null : reqOf(row, l);
		if (dr && dr.statusId === DR_ACEPTADO) return discountFrom(row.listAmount, dr.calcType, dr.value);
		return -1; // sin descuento nuevo: se queda el precio que ya trae el renglón
	};
	/*
		R4 (Esteban, 2026-10-08): la fecha que cuenta para el recargo es la FECHA DE PAGO del comprobante. Si la fecha es el día
		del vencimiento o antes y el pago liquida la mensualidad, el recargo que ya se generó se anula solo y la mensualidad
		conserva su descuento. Aquí solo se enseña; el servidor lo decide y lo deja en el historial y en Auditoría.
	*/
	const aTiempo = (row: Row): boolean => row.typeId === 1 && !!row.surcharge && !!row.surcharge.onTimeCancellable
		&& !!row.surcharge.dueDate && !!paymentDate && paymentDate <= row.surcharge.dueDate;
	const pendienteATiempo = (row: Row): number => round2(row.subTotal - row.paidAmount);
	/** El recargo de una mensualidad que se paga con comprobante a tiempo: no se cobra (se anula al registrar). */
	const recargoAnulado = (p: Person, row: Row): boolean => {
		if (row.typeId !== 2 || !row.parentKey) return false;
		const parent = p.rows.find((x) => x.key === row.parentKey);
		return !!parent && aTiempo(parent);
	};
	/** Cambio de promoción confirmado: los pendientes que vuelven a precio normal (sin la promo vieja) ya no se cobran al precio del acuerdo. */
	const repreciados = useRef<Set<string>>(new Set());
	const dueOf = (p: Person, row: Row, l: Line): number => {
		if (recargoAnulado(p, row)) return 0;
		if (promoKeyFor(p, row)) {
			// Mes reabierto que seguía abonado: lo que debe hoy + la diferencia que vuelve.
			const reabierto = reopenedOf(p, row);
			if (reabierto !== null) return round2(row.pending + reabierto);
			// «Todas juntas»: el servidor pone el descuento de la promo en estos renglones (no se manda promotionId); se cobra ya con el descuento.
			if (promoApp!.option.kind === "todos_juntos" && row.subTotal >= row.listAmount - 0.009) {
				const promo = p.promos.find((x) => x.id === promoApp!.option.promotionId);
				if (promo) return round2(row.listAmount - discountFrom(row.listAmount, promo.calcType, promo.value) - row.paidAmount);
				const base = persons.find((x) => x.student.id === promoApp!.studentId)?.rows.filter((r) => promoApp!.option.payKeys.includes(r.key)).reduce((a, r) => a + r.listAmount - r.paidAmount, 0) || 0;
				return base > 0.009 ? round2((row.listAmount - row.paidAmount) * promoApp!.option.needed / base) : round2(row.listAmount - row.paidAmount);
			}
			// Cambio de promoción: los meses sin pagos vuelven a precio de lista.
			if (promoApp!.option.replaces) return round2(row.listAmount - row.paidAmount);
		}
		if (aTiempo(row)) return pendienteATiempo(row);
		const d = discountOf(p, row, l);
		if (d < 0 && repreciados.current.has(p.student.id + ":" + row.key)) return round2(row.listAmount - row.paidAmount);
		return d < 0 ? row.pending : round2(row.listAmount - d - row.paidAmount);
	};
	const applyOf = (p: Person, row: Row, l: Line): number => (!l.on || recargoAnulado(p, row) ? 0 : (l.touched && !lockedFor(p, row) ? round2(Number(l.apply) || 0) : dueOf(p, row, l)));
	const stateOf = (p: Person, row: Row, l: Line): "full" | "partial" | "zero" | "none" => {
		const due = dueOf(p, row, l);
		const a = applyOf(p, row, l);
		if (due <= 0.009) return "zero";
		if (a >= due - 0.009) return "full";
		return a > 0.009 ? "partial" : "none";
	};

	/*
		A2' (Esteban, 2026-10-08): un mes regalado de un acuerdo pendiente de pago adelantado se cobra a precio normal (se marca
		y se aplica como cualquier otro). Queda en $0 saldado SOLO cuando el servidor registra el pago adelantado completo (lo
		que falte de las mensualidades que pagan, de una vez) en esa misma transacción. Aquí no se bloquea nada.
	*/
	const regaloOk = (_list: Person[], _row: Row): boolean => true;
	const esRegalo = (row: Row) => !!row.agreement && row.agreement.state === "regalo" && !row.agreement.conditionMet;
	const ajustarRegalos = (list: Person[]): Person[] => list;
	/**
		2x1 cuyo ticket está a nombre de quien RECIBE (Esteban 2026-10-30): el draft dice quién paga; se agrega esa persona al pago y su
		panel «Aplicar Promoción» abre precargado.
	*/
	const abrirEnQuienPaga = (d: PromoDraft) => {
		const sid = Number(d.studentId);
		if (!sid) return;
		setPromoQuitada(false);
		setPromoPreload({ studentId: sid, draft: d, key: "draft" + sid + "-" + d.enrollmentId + "-" + d.promotionId });
		if (!persons.some((x) => x.student.id === sid)) addPerson({ id: sid, name: d.studentName || "", identityCode: d.identityCode || undefined } as RegisterStudent);
	};
	/** Adelanto pendiente de una promo de adelanto: abre «Aplicar Promoción» con ese acuerdo precargado (ya no se marca a mano). */
	const aplicarAdelanto = (studentId: number, adv: Advance) => {
		if (!adv.promotionId || !adv.baseEnrollmentId) return;
		setPromoQuitada(false);
		setPromoApp((cur) => (cur && cur.studentId === studentId ? null : cur));
		setPromoPreload({ studentId, key: "adv" + adv.agreementId + "-" + Date.now(),
			draft: { enrollmentId: adv.baseEnrollmentId, promotionId: adv.promotionId, giftEnrollmentId: adv.giftEnrollmentId || null,
				giftStudentId: adv.giftEnrollmentId ? adv.giftStudentId : null, agreementId: adv.agreementId } });
	};
	/** Marca completas, de una vez, las mensualidades que faltan del pago adelantado (el servidor salda los regalos al registrar). */
	const marcarAdelanto = (studentId: number, adv: Advance) => {
		setPersons((prev) => prev.map((p) => {
			if (p.student.id !== studentId) return p;
			const lines = { ...p.lines };
			adv.keys.forEach((k) => { if (lines[k]) lines[k] = { ...lines[k], on: true, touched: false, apply: "" }; });
			return { ...p, lines };
		}));
	};

	/*
		Cambio de promoción (Esteban, 2026-10-27): una sola promo de mensualidades por matrícula. Al elegir una de mensualidad (o un kit
		que pide meses pagados) WAPP pregunta al servidor si reemplaza otra; si sí, la cajera lo confirma aquí y la diferencia de los meses
		que se reabren va en este mismo cobro. El servidor lo vuelve a revisar y lo hace dentro del pago (y lo manda a Auditoría).
	*/
	const previas = useRef<Record<string, Cambio | null | "cargando">>({});
	const [, setVerPrevias] = useState(0);
	const [confirmados, setConfirmados] = useState<Record<string, boolean>>({});
	const combos = persons.flatMap((p) => p.rows.filter((row) => p.lines[row.key]?.on && p.lines[row.key]?.promotionId).map((row) => ({ p, row, pid: p.lines[row.key].promotionId as number })))
		.filter(({ p, pid }) => { const pr = p.promos.find((x) => x.id === pid); return !!pr && (pr.group === "mensualidad" || (pr.appliesTo === "article" && !!pr.rules?.requirePaidMonths)); });
	const comboKeys = combos.map((c) => c.p.student.id + "|" + c.row.key + "|" + c.pid);
	useEffect(() => {
		combos.forEach((c, i) => {
			const k = comboKeys[i];
			if (k in previas.current) return;
			previas.current[k] = "cargando";
			http.paymentService.promoChange(campusId, c.p.student.id, c.row.key, c.pid, intentId).then((r: any) => {
				const d = r.data || {};
				previas.current[k] = d.needed ? { k: d.studentId + ":" + d.enrollmentId + ":" + d.promotionId, studentId: d.studentId, enrollmentId: d.enrollmentId, promotionId: d.promotionId,
					promoName: d.promoName, text: d.text, blocked: d.blocked || null, months: d.months || [], repriced: d.repriced || [], difference: Number(d.difference) || 0 } : null;
				setVerPrevias((n) => n + 1);
			}).catch((e: any) => { delete previas.current[k]; setError(serverError(e)); });
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [comboKeys.join(",")]);
	const cambios: Cambio[] = Object.values(Object.fromEntries(comboKeys.map((k) => previas.current[k]).filter((x): x is Cambio => !!x && x !== "cargando").map((c) => [c.k, c])));
	const revisandoCambios = comboKeys.some((k) => previas.current[k] === "cargando");
	/** Lo que cada cambio confirmado suma al cobro: la diferencia de los meses reabiertos (si el mes ya estaba marcado, lo que le falta además). */
	const extraDeCambio = (c: Cambio): number => {
		const p = persons.find((x) => x.student.id === c.studentId);
		return round2(c.months.reduce((s, m) => {
			const row = p?.rows.find((x) => x.key === m.key);
			const ya = row && p && p.lines[row.key]?.on ? applyOf(p, row, p.lines[row.key]) : 0;
			return s + m.pendingAfter - ya;
		}, 0));
	};
	// Un mismo mes no se suma dos veces aunque dos promos de la matrícula lo reabran (el servidor deja una sola promo de mensualidades).
	const confirmadosLista = cambios.filter((c) => confirmados[c.k]);
	repreciados.current = new Set(confirmadosLista.flatMap((c) => c.repriced.map((m) => c.studentId + ":" + m.key)));
	const extraCambios = round2(confirmadosLista.reduce((s, c, i) => s + extraDeCambio({ ...c,
		months: c.months.filter((m) => !confirmadosLista.slice(0, i).some((o) => o.studentId === c.studentId && o.months.some((x) => x.key === m.key))) }), 0));

	const received = round2(Number(total) || 0);
	/** Meses reabiertos que no están entre los renglones cobrables: su diferencia se suma al Aplicado. */
	const promoExtra = round2(reopenedLoose().reduce((acc, m) => acc + (Number(m.difference) || 0), 0));
	const applied = round2(persons.reduce((acc, p) => acc + p.rows.reduce((s, row) => s + applyOf(p, row, p.lines[row.key] || ({} as Line)), 0), 0) + extraCambios + promoExtra);
	const left = round2(received - applied);

	// Lo sugerido: solo lo que el monto cubre COMPLETO, en orden (ticket, vencido, por fecha). Se detiene en el primero que no cabe.
	const suggest = (list: Person[], amount: number): Person[] => {
		let restante = round2(amount);
		let stop = false;
		const order: { p: number; row: Row }[] = [];
		list.forEach((p, pi) => p.rows.forEach((row) => order.push({ p: pi, row })));
		order.sort((a, b) => (a.p - b.p) || (Number(b.row.isTicket) - Number(a.row.isTicket)) || (Number(b.row.overdue) - Number(a.row.overdue))
			|| String(a.row.dueDate || "9999").localeCompare(String(b.row.dueDate || "9999")) || (a.row.kind === b.row.kind ? 0 : a.row.kind === "tuition" ? -1 : 1)
			|| ((a.row.monthIndex || 0) - (b.row.monthIndex || 0)) || ((a.row.typeId ?? 9) - (b.row.typeId ?? 9)));
		const next = list.map((p) => ({ ...p, lines: Object.fromEntries(Object.entries(p.lines).map(([k, l]) => [k, { ...l, on: false, touched: false, apply: "" }])) as Record<string, Line> }));
		// Los renglones de la promo van marcados y completos antes que cualquier otro (los meses reabiertos sueltos también cuentan).
		restante = round2(restante - reopenedLoose().reduce((acc, m) => acc + (Number(m.difference) || 0), 0));
		const ec = promoApp?.enCamino || null;
		// En Camino: sus recargos van primero y completos (el servidor los cobra aparte, fuera del reparto en orden), solo si caben; lo que
		// queda es lo que se reparte en los meses de la promo. Así la suma de los renglones nunca pasa de lo recibido.
		const recargosEc = new Set<string>();
		if (ec) next.forEach((person) => person.rows.forEach((row) => {
			const l = person.lines[row.key];
			if (!l || !promoRecargo(person, row)) return;
			recargosEc.add(person.student.id + ":" + row.key);
			const due = recargoAnulado(person, row) ? 0 : dueOf(person, row, l);
			l.on = !recargoAnulado(person, row) && due > 0.009 && due <= restante + 0.009;
			if (l.on) restante = round2(restante - due);
		}));
		// En Camino: el dinero entra en orden sobre los renglones de la promo (`payKeys`); el último puede quedar a medias (abono).
		const enOrden = new Set<string>();
		if (ec) next.filter((person) => person.student.id === promoApp!.studentId).forEach((person) => {
			let corte = false;
			(promoApp!.option.payKeys || []).forEach((k) => {
				const row = person.rows.find((r) => r.key === k);
				const l = person.lines[k];
				if (!row || !l) return;
				enOrden.add(k);
				if (frozenFor(person, row) || corte) return;
				const due = dueOf(person, row, l);
				if (due <= 0.009) return;
				if (due <= restante + 0.009) { l.on = true; restante = round2(restante - due); }
				else if (restante > 0.009) { l.on = true; l.touched = true; l.apply = String(restante); restante = 0; corte = true; }
				else corte = true;
			});
		});
		next.forEach((person) => person.rows.forEach((row) => {
			if (!person.lines[row.key]) return;
			if (enOrden.has(row.key) || recargosEc.has(person.student.id + ":" + row.key)) return;
			if (lockedFor(person, row)) {
				person.lines[row.key].on = true;
				restante = round2(restante - dueOf(person, row, person.lines[row.key]));
			} else if (promoRecargo(person, row)) {
				// Los recargos de los meses de la promo van en el mismo pago (salvo los que se anulan por pagar a tiempo).
				person.lines[row.key].on = !recargoAnulado(person, row);
				restante = round2(restante - dueOf(person, row, person.lines[row.key]));
			}
		}));
		order.forEach(({ p, row }) => {
			if (stop) return;
			const person = next[p];
			const l = person.lines[row.key];
			if (lockedFor(person, row) || promoRecargo(person, row) || enOrden.has(row.key) || frozenFor(person, row)) return;
			const due = dueOf(person, row, l);
			if (due <= 0.009) return;
			if (due <= restante + 0.009) {
				l.on = true;
				restante = round2(restante - due);
			} else {
				stop = true;
			}
		});
		// Un renglón en $0 (inscripción gratis) va con su matrícula cuando algo de ella va marcado.
		next.forEach((person) => person.rows.forEach((row) => {
			const l = person.lines[row.key];
			if (!esRegalo(row) && !recargoAnulado(person, row) && !frozenFor(person, row) && dueOf(person, row, l) <= 0.009 && person.rows.some((o) => o.enrollmentId === row.enrollmentId && o.key !== row.key && person.lines[o.key].on)) l.on = true;
		}));
		return ajustarRegalos(next);
	};
	// Lo que debe cada renglón (cambia con una promo o un especial aceptado): si cambia, se vuelve a sugerir.
	const dueSig = persons.map((p) => p.loading ? "x" : p.rows.map((row) => p.lines[row.key] ? dueOf(p, row, p.lines[row.key]) : 0).join(",")).join("|");
	useEffect(() => {
		if (manual || persons.some((p) => p.loading)) return;
		setPersons((prev) => suggest(prev, received));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [received, persons.length, dueSig, promoApp?.option.promotionId, promoApp?.option.enrollmentId]);

	// Al escoger la promo (o al terminar de cargar la persona) sus renglones quedan marcados y completos.
	const lockSig = promoApp ? promoApp.studentId + ":" + (promoApp.enCamino ? "ec" + promoApp.enCamino.id + (promoApp.enCamino.frozenKeys || []).join(",") + ":" : "") + promoApp.option.payKeys.join(",") + ":" + (promoApp.option.surchargeKeys || []).join(",") : "";
	useEffect(() => {
		if (!promoApp) return;
		setPersons((prev) => {
			let changed = false;
			const next = prev.map((p) => {
				if (p.student.id !== promoApp.studentId || p.loading) return p;
				const lines = { ...p.lines };
				// En Camino: nada va completo a la fuerza (los renglones los reparte `suggest` en orden); lo apartado se apaga.
				if (promoApp.enCamino) (promoApp.enCamino.frozenKeys || []).forEach((k) => {
					const l = lines[k];
					if (l && (l.on || l.touched)) { lines[k] = { ...l, on: false, touched: false, apply: "" }; changed = true; }
				});
				if (!promoApp.enCamino) promoApp.option.payKeys.forEach((k) => {
					const l = lines[k];
					if (l && (!l.on || l.touched || l.promotionId)) { lines[k] = { ...l, on: true, touched: false, apply: "", promotionId: null }; changed = true; }
				});
				// Sus recargos vencidos entran al pago como renglones propios (no se bloquean: Supervisión puede eximirlos). En Camino los
				// marca `suggest` (primero y solo si caben en lo recibido).
				if (!promoApp.enCamino) (promoApp.option.surchargeKeys || []).forEach((k) => {
					const l = lines[k];
					const hijo = p.rows.find((r) => r.key === k);
					const on = !(hijo && recargoAnulado(p, hijo));
					if (l && (l.on !== on || l.touched)) { lines[k] = { ...l, on, touched: false, apply: "" }; changed = true; }
				});
				return changed ? { ...p, lines } : p;
			});
			return changed ? next : prev;
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [lockSig, persons.map((p) => (p.loading ? "1" : "0")).join("")]);

	const elegirPromo = (studentId: number, option: PromoOption, d: PromoDraft | null, ec: EnCamino | null = null) => {
		// 2x1 con el curso regalado ya conocido (draft o acuerdo pendiente): llega elegido.
		const giftE = d?.giftEnrollmentId ? Number(d.giftEnrollmentId) : ec?.giftEnrollmentId ? Number(ec.giftEnrollmentId) : (option.giftEnrollmentId ? Number(option.giftEnrollmentId) : null);
		const giftS = d?.giftStudentId ? Number(d.giftStudentId) : (option.giftStudentId ? Number(option.giftStudentId) : null);
		// En Camino: las asesoras ya están en la promo; el curso regalado también.
		const advEc = ec && (ec.advisors || []).length ? (ec.advisors || []).map((a) => ({ userId: String(a.userId), name: a.name || "", sharePercentage: a.sharePercentage ?? null })) : null;
		setPromoApp({ studentId, option, enCamino: ec,
			advisors: advEc || (d && Array.isArray(d.advisors) ? d.advisors.map((a: any) => ({ userId: String(a.userId), name: a.name || "", sharePercentage: a.sharePercentage ?? null })) : null),
			giftStudentId: giftS, giftEnrollmentId: giftE,
			giftLabel: d?.giftStudentName ? [d.giftStudentName, cursoCorto(d.giftCourseName || "")].filter(Boolean).join(" ")
				: ec?.giftStudentName ? [ec.giftStudentName, cursoCorto(ec.giftCourseName || "")].filter(Boolean).join(" ") : "",
			giftIssues: ec ? [] : null,
			giftOptionKey: initialGiftKey(option, d?.giftOptionKey) });
		setPromoOpen(null);
		setPromoQuitada(false);
		previas.current = {};
		// En Camino: Caja escribe el monto; el reparto en orden se recalcula desde cero.
		if (ec) setManual(false);
		else if ((Number(total) || 0) < 0.005 && chargeOf(option) > 0.005) setTotal(String(chargeOf(option)));
	};

	const setLine = (studentId: number, key: string, fn: (l: Line, row: Row, p: Person) => Line) => {
		setManual(true);
		setPersons((prev) => prev.map((p) => {
			if (p.student.id !== studentId) return p;
			const row = p.rows.find((r) => r.key === key)!;
			if (lockedFor(p, row) || frozenFor(p, row)) return p;
			return { ...p, lines: { ...p.lines, [key]: fn(p.lines[key], row, p) } };
		}));
	};

	/** Marcar un renglón marca también los renglones en $0 de su matrícula (la inscripción gratis va con su mes). */
	const toggleRow = (studentId: number, key: string, on: boolean) => {
		const p0 = persons.find((x) => x.student.id === studentId);
		const r0 = p0?.rows.find((r) => r.key === key);
		if (p0 && r0 && (lockedFor(p0, r0) || frozenFor(p0, r0))) return;
		setManual(true);
		setPersons((prev) => ajustarRegalos(prev.map((p) => {
			if (p.student.id !== studentId) return p;
			const row = p.rows.find((r) => r.key === key)!;
			const lines = { ...p.lines, [key]: { ...p.lines[key], on, touched: false, apply: "" } };
			// El recargo va con su mensualidad: se marca y desmarca con ella (la cajera puede desmarcar solo el recargo).
			if (row.surchargeKey && lines[row.surchargeKey]) lines[row.surchargeKey] = { ...lines[row.surchargeKey], on: on && !aTiempo(row), touched: false, apply: "" };
			if (on && row.enrollmentId) {
				p.rows.forEach((o) => { if (!esRegalo(o) && !recargoAnulado(p, o) && o.enrollmentId === row.enrollmentId && o.key !== key && dueOf(p, o, lines[o.key]) <= 0.009) lines[o.key] = { ...lines[o.key], on: true }; });
			}
			return { ...p, lines };
		})));
	};

	// ------------------------------------------------------------------ referencia

	const checkReference = async () => {
		const ref = reference.trim();
		if (!method || !method.referenceRequired || ref.length < 4 || !accountId) { setRefCheck(null); return; }
		try {
			const r = await http.paymentService.checkReference(campusId, { accountId, paymentMethodId: methodId, reference: ref, paymentDate, total: received });
			setRefCheck(r.data);
		} catch (e) {
			setRefCheck(null);
		}
	};
	useEffect(() => { checkReference(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [accountId, methodId, paymentDate, received]);

	// ------------------------------------------------------------------ especial

	const unaVez = useUnaALaVez();
	const sendSpecial = () => unaVez(sendSpecialAhora);
	const sendSpecialAhora = async () => {
		if (!special) return;
		try {
			setBusy(true);
			setError(null);
			const r = await http.paymentService.requestDiscount(campusId, { studentId: special.studentId, rowKey: special.key, calcType: special.calcType,
				value: Number(special.value), reason: special.reason.trim(), intentId });
			const dr: DReq = r.data;
			setPersons((prev) => prev.map((p) => p.student.id !== special.studentId ? p : {
				...p, rows: p.rows.map((row) => row.key === special.key ? { ...row, discountRequest: dr } : row),
				lines: { ...p.lines, [special.key]: { ...p.lines[special.key], promotionId: null, drId: dr.id, touched: false } }
			}));
			setSpecial(null);
			setNotice(R.specialSent);
			if (intentId) saveDraft(true);
		} catch (e) {
			setError(serverError(e));
		} finally {
			setBusy(false);
		}
	};

	// ------------------------------------------------------------------ eximir recargo

	/** La cajera no exime: arma el recargo, ve lo que pasa y queda Por Aprobar (fase 2). Supervisión sigue eximiendo aquí mismo. */
	const aprobacion = useEnviarAprobacion(campusId);
	const pedirEximirRecargo = () => {
		if (!waiver) return;
		const w = waiver;
		aprobacion.pedir({
			kind: "eximir_recargo", titulo: R.waiveTitle, motivoInicial: w.reason.trim(),
			data: { surchargeId: w.surchargeId, calcType: w.calcType, value: Number(w.value), reason: w.reason.trim(), intentId },
			onDone: async (r) => { setWaiver(null); setNotice(r.text); await refreshPerson(w.studentId); },
		});
	};

	const sendWaiver = () => unaVez(sendWaiverAhora);
	const sendWaiverAhora = async () => {
		if (!waiver) return;
		try {
			setBusy(true);
			setError(null);
			const payload = { calcType: waiver.calcType, value: Number(waiver.value), reason: waiver.reason.trim() };
			if (waiver.mode === "waive") {
				await http.paymentService.waiveSurcharge(campusId, waiver.surchargeId, payload);
				setNotice(R.waived);
			} else {
				await http.paymentService.requestSurchargeWaiver(campusId, waiver.surchargeId, { ...payload, intentId });
				setNotice(R.waiverSent);
			}
			const sid = waiver.studentId;
			setWaiver(null);
			await refreshPerson(sid);
		} catch (e) {
			setError(serverError(e));
		} finally {
			setBusy(false);
		}
	};

	// ------------------------------------------------------------------ borrador (ticket)

	const draftPayload = () => ({ total, methodId, accountId, paymentDate, reference, payerName, payerPhone, notes, ...(variosDepositos ? { fotos } : {}),
		promo: promoApp ? { studentId: promoApp.studentId, enrollmentId: promoApp.option.enrollmentId, promotionId: promoApp.option.promotionId, advisors: promoApp.advisors,
			giftEnrollmentId: promoApp.giftEnrollmentId, giftStudentId: promoApp.giftStudentId, giftOptionKey: promoApp.giftOptionKey,
			...(promoApp.enCamino ? { enCaminoId: promoApp.enCamino.id } : {}) } : null,
		persons: persons.map((p) => ({ student: { id: p.student.id, name: nameOf(p.student), identityCode: p.student.identityCode }, notes: p.notes,
			lines: Object.fromEntries(Object.entries(p.lines).filter(([, l]) => l.on || l.promotionId || l.drId)) })) });
	const saveDraft = async (silent?: boolean) => {
		if (!intentId) return;
		try {
			await http.paymentIntentsService.saveDraft(campusId, intentId, draftPayload());
			if (!silent) setNotice(R.draftSaved);
		} catch (e) {
			setError(serverError(e));
		}
	};

	// ------------------------------------------------------------------ registrar

	const payPersons = persons.filter((p) => p.rows.some((row) => p.lines[row.key]?.on));
	const ticketSinAlumno = !!ticket && !(Number(ticket.studentId) > 0);
	const ownerId: number = ticketSinAlumno ? (payPersons.some((p) => p.student.id === owner) ? owner : (payPersons[0]?.student.id || 0)) : 0;
	const onLines = persons.flatMap((p) => p.rows.filter((row) => p.lines[row.key]?.on).map((row) => ({ p, row, l: p.lines[row.key] })));
	const pendingSpecial = onLines.some(({ p, row, l }) => { const dr = promoKeyFor(p, row) ? null : reqOf(row, l); return !!dr && dr.statusId !== DR_ACEPTADO; });
	const filesNeeded = !!method?.attachmentRequired && files.length + ticketDocs.length < 1;
	// Limpieza sin hacer: la mensualidad marcada de esa matrícula no se cobra (el servidor también lo frena).
	const limpSinHacer = onLines.find(({ p, row }) => row.kind === "tuition" && row.typeId === 1 && (p.cleanupPending || []).some((c: any) => c.enrollmentId === row.enrollmentId));
	// «Registrar Pago» never greys out for this: clicking it opens that case's review (Esteban, 2026-11-02).
	const limpCaso: any = limpSinHacer ? (limpSinHacer.p.cleanupPending || []).find((c: any) => c.enrollmentId === limpSinHacer.row.enrollmentId) : null;
	const whyNot: string | null =
		limpSinHacer ? "Primero revisa la promoción de " + (limpSinHacer.row.courseName || "su curso") + "."
		: received <= 0 || onLines.length < 1 ? R.why.rows
			: Math.abs(left) >= 0.005 ? R.why.left
				: !methodId || !accountId ? R.why.method
					: method?.referenceRequired && reference.trim().length < 4 ? R.why.reference
						: refCheck && !refCheck.ok ? (refCheck.message || R.why.reference)
							: notes.trim().length < 5 ? R.why.notes
								: filesNeeded ? R.why.files
								: adjuntos?.duplicates.length ? adjuntos.duplicates[0]
								: adjuntos?.loading ? R.aiReading
									: pendingSpecial ? R.why.pending

										//Ticket sin alumno en WAPP (/pagar): Caja elige a la persona y el servidor liga el ticket al dueño del comprobante (intentStudentId).
										: ticket && !(Number(ticket.studentId) > 0) && persons.length === 0 ? R.why.ticketNoStudent
										: variosDepositos && fotos.length < 1 ? R.why.photos
										: ticket && Number(ticket.studentId) > 0 && !variosDepositos && !persons.some((p) => p.student.id === Number(ticket.studentId)) ? R.why.ticketStudent
											: promoApp?.option.paidAlready ? "Aplica la Promoción Sin Cobrar o Quítala"
											: promoApp && !promoApp.enCamino && promoPending(promoApp) ? promoPending(promoApp)
											: promoApp?.enCamino && promoApp.option.blocked ? promoApp.option.blocked
											: revisandoCambios ? "Revisando la promoción…"
											: cambios.find((c) => c.blocked) ? cambios.find((c) => c.blocked)!.blocked
											: cambios.some((c) => !confirmados[c.k]) ? "Confirma el cambio de promoción (en la persona) o quita la promo."
											: null;

	const submit = () => {
		if (limpCaso) { if (!busy) setRevisandoLimp(limpCaso); return; }
		unaVez(() => submitAhora());
	};
	const submitAhora = async (motivoSinPromo?: string) => {
		if (whyNot || busy) return;
		try {
			setBusy(true);
			setError(null);
			setPreguntaPromo(null);
			const payload = {
				total: received, paymentMethodId: methodId, accountId, paymentDate, reference: method?.referenceRequired ? reference.trim() : "",
				notes: notes.trim(), payerName: payerName.trim(), payerPhone: payerPhone.trim(), clientKey: clientKey.current, intentId,
				...(ownerId ? { intentStudentId: ownerId } : {}),
				...(variosDepositos ? { ticketDocs: fotos } : {}),
				attachedFiles: files,
				persons: payPersons.map((p) => {
					const rows: any[] = p.rows.filter((row) => p.lines[row.key]?.on && !recargoAnulado(p, row) && !frozenFor(p, row)).map((row) => {
						const l = p.lines[row.key];
						const dr = promoKeyFor(p, row) ? null : reqOf(row, l);
						return { key: row.key, apply: applyOf(p, row, l), promotionId: l.promotionId || null,
							discountRequestId: !l.promotionId && dr && dr.statusId === DR_ACEPTADO ? dr.id : null, discountNote: l.note.trim() };
					});
					// Meses reabiertos por el cambio de promoción: van completos (con su diferencia).
					cambios.filter((c) => confirmados[c.k] && c.studentId === p.student.id).forEach((c) => c.months.forEach((mes) => {
						const ya = rows.find((x) => x.key === mes.key);
						if (ya) { ya.apply = mes.pendingAfter; ya.promotionId = null; ya.discountRequestId = null; }
						else rows.push({ key: mes.key, apply: mes.pendingAfter, promotionId: null, discountRequestId: null, discountNote: "" });
					}));
					// Meses que la promo reabre y que no están entre los renglones cobrables: van como renglón con su diferencia.
					if (promoApp && promoApp.studentId === p.student.id) reopenedLoose().forEach((m) => {
						if (!rows.some((x) => x.key === m.key)) rows.push({ key: m.key, apply: round2(Number(m.difference) || 0), promotionId: null, discountRequestId: null, discountNote: "" });
					});
					return { studentId: p.student.id, notes: p.notes.trim(), rows };
				}),
				promoChanges: cambios.filter((c) => confirmados[c.k]).map((c) => ({ studentId: c.studentId, enrollmentId: c.enrollmentId, promotionId: c.promotionId })),
				...(promoApp ? { promoApplication: {
					enrollmentId: promoApp.option.enrollmentId, promotionId: promoApp.option.promotionId,
					advisors: (promoApp.advisors || []).map((a) => ({ userId: a.userId, name: a.name, sharePercentage: a.sharePercentage })),
					...(promoApp.giftStudentId ? { giftStudentId: promoApp.giftStudentId } : {}),
					...(promoApp.giftEnrollmentId ? { giftEnrollmentId: promoApp.giftEnrollmentId } : {}),
					...(promoApp.giftOptionKey ? { giftOptionKey: promoApp.giftOptionKey } : {}),
					...(promoApp.enCamino ? { enCaminoId: promoApp.enCamino.id } : {}),
					origin: draft?.origin || (ticket ? "ticket" : "cobros") } } : {}),
				...(motivoSinPromo ? { promoAuto: false, promoAutoReason: motivoSinPromo } : {})
			};
			const r = await http.paymentService.register(campusId, payload);
			setResult(r.data);
			setSinPromo(null);
		} catch (e) {
			const msg = serverError(e);
			if (msg.startsWith(PROMO_EN_NOTA)) setPreguntaPromo(msg.slice(PROMO_EN_NOTA.length).trim());
			else setError(msg);
			clientKey.current = newKey();
		} finally {
			setBusy(false);
		}
	};

	const openReceipt = (id: number) => abrirArchivo({ nombre: `Recibo del pago ${id}.pdf`, cargar: () => http.fileService.getReceiptPDF(campusId, id).then((r: any) => new Blob([r.data], { type: "application/pdf" })) });
	const finish = () => { if (result) onSuccess(result); else onClose(); };

	// ------------------------------------------------------------------ vista

	const lab = (t: string) => <span style={{ fontSize: 12, fontWeight: 700, color: "var(--wa-apagado)", display: "block", marginBottom: 3 }}>{t}</span>;
	const pill = (t: string, tone: "oro" | "acento" | "filo") => (
		<span style={{ fontSize: 11, fontWeight: 700, padding: "1px 7px", borderRadius: 999, whiteSpace: "nowrap", marginLeft: 6,
			background: tone === "oro" ? "var(--wa-oro-suave)" : tone === "acento" ? "var(--wa-acento-suave)" : "var(--wa-papel)",
			color: tone === "oro" ? "var(--wa-oro)" : tone === "acento" ? "var(--wa-acento-texto)" : "var(--wa-apagado)",
			border: tone === "filo" ? "1px solid var(--wa-filo)" : "none" }}>{t}</span>
	);
	const stateTag = (s: string) => s === "full" ? pill(R.states.full, "acento") : s === "partial" ? pill(R.states.partial, "oro") : s === "zero" ? pill(R.states.zero, "filo") : s === "none" ? pill(R.states.none, "oro") : null;

	const blockPago = (
		<div>
			<h3 style={{ fontSize: 16, margin: "0 0 8px" }}>{R.paymentBlock} <small style={{ fontWeight: 500, color: "var(--wa-apagado)", fontSize: 13 }}>{R.paymentBlockHint}</small></h3>
			<div style={{ display: "grid", gridTemplateColumns: movil ? "1fr 1fr" : "repeat(3, minmax(0,1fr))", gap: 10 }}>
				<label style={LB}>{lab(R.received + " (" + currencySymbol + ")")}
					<input className="form-control" type="number" min="0" step="0.01" value={total} disabled={busy} style={{ fontWeight: 700 }}
						onChange={(e) => setTotal(e.target.value)} /></label>
				<label style={LB}>{lab(R.date)}
					<input className="form-control" type="date" value={paymentDate} max={hoyCdmx()} disabled={busy || !!method?.dateLocked || fechaFija} onChange={(e) => setPaymentDate(e.target.value)} /></label>
				<label style={LB}>{lab(R.method)}
					<select className="form-select" value={methodId} disabled={busy} onChange={(e) => setMethodId(Number(e.target.value))}>
						{methods.map((m: any) => <option key={"m" + m.id} value={m.id}>{m.name}</option>)}
					</select></label>
				<label style={LB}>{lab(R.account)}
					<select className="form-select" value={accountId} disabled={busy || accounts.length < 1} onChange={(e) => setAccountId(Number(e.target.value))}>
						{accounts.length !== 1 ? <option value={0}>—</option> : ""}
						{accounts.map((a: any) => <option key={"a" + a.id} value={a.id}>{a.name}</option>)}
					</select></label>
				{method?.referenceRequired ? (
					<label style={LB}>{lab(R.reference)}
						<input className="form-control" value={reference} disabled={busy} onChange={(e) => { setReference(e.target.value); setRefCheck(null); }} onBlur={checkReference} />
					</label>
				) : ""}
				<label style={LB}>{lab(R.payer)}<input className="form-control" value={payerName} disabled={busy} onChange={(e) => setPayerName(e.target.value)} /></label>
				<label style={LB}>{lab(R.payerPhone)}<input className="form-control" value={payerPhone} disabled={busy} onChange={(e) => setPayerPhone(e.target.value)} /></label>
				<label style={{ ...LB, gridColumn: movil ? "1 / -1" : "span 2" }}>{lab(R.notes)}
					<input className="form-control" value={notes} disabled={busy} onChange={(e) => setNotes(e.target.value)} /></label>
				<label style={{ ...LB, gridColumn: movil ? "1 / -1" : undefined }}>{lab(R.files + (ticketDocs.length ? " (" + R.ticketFiles(ticketDocs.length) + ")" : ""))}
					{/* Botón propio en español (el del navegador decía «Choose Files / No file chosen»). */}
					<span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
						<span className={"btn btn-outline-secondary btn-sm rounded-pill" + (busy ? " disabled" : "")} style={{ minHeight: 40, display: "inline-flex", alignItems: "center", gap: 8 }}><FontAwesomeIcon icon={solid("paperclip")} />Elegir Archivos</span>
						<span style={{ fontSize: 13, color: "var(--wa-apagado)", minWidth: 0, overflowWrap: "anywhere" }}>
							{files.length ? files.map((f) => f.name).join(", ") : "Ningún archivo elegido"}</span>
					</span>
					<input type="file" multiple accept="image/png,image/jpeg,image/webp,application/pdf" disabled={busy} style={{ display: "none" }}
						onChange={(e) => setFiles(Array.from(e.target.files || []))} /></label>
			</div>
			{variosDepositos ? (
				<div style={{ marginTop: 10 }}>
					{lab(R.photosTitle)}
					<div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
						{ticketDocs.map((d: any) => {
							const k = Number(d.index);
							const leido = (ai?.readings || []).find((x: any) => Number(x.index) === k);
							return (
								<label key={"fp" + k} style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid " + (fotos.includes(k) ? "var(--wa-acento)" : "var(--wa-filo)"),
									background: fotos.includes(k) ? "var(--wa-acento-suave)" : d.receiptId ? "var(--wa-papel)" : "var(--wa-superficie)", padding: "8px 12px", minHeight: 44,
									borderRadius: 8, position: "static", margin: 0, cursor: d.receiptId || busy ? "default" : "pointer", flex: movil ? "1 1 100%" : "0 1 auto", minWidth: 0 }}>
									<input type="checkbox" checked={fotos.includes(k)} disabled={!!d.receiptId || busy} style={{ accentColor: "var(--wa-acento)", margin: 0, flex: "0 0 auto" }}
										onChange={() => { fotosTocadas.current = true; setFotos((l) => l.includes(k) ? l.filter((x) => x !== k) : [...l, k]); if (preview?.index !== k) showDoc(k); }} />
									<span style={{ fontSize: 14, minWidth: 0, overflowWrap: "anywhere", lineHeight: 1.35 }}><b style={{ fontWeight: 600 }}>{d.name}</b>
										{d.receiptId ? <span style={{ color: "var(--wa-apagado)" }}> · {R.photoRegistered(d.receiptId)}</span>
											: leido?.summary ? <span style={{ color: "var(--wa-apagado)" }}> · {leido.summary}</span> : null}</span>
								</label>
							);
						})}
					</div>
					<div style={{ fontSize: 13, color: fotos.length < fotosLibres.length ? "var(--wa-oro)" : "var(--wa-apagado)", fontWeight: fotos.length < fotosLibres.length ? 600 : 400, marginTop: 6 }}>
						{fotos.length < fotosLibres.length ? R.photosOpen(fotosLibres.length - fotos.length) : R.photosHint}</div>
				</div>
			) : null}
			{method?.referenceRequired && refCheck ? (
				<div style={{ fontSize: 13, fontWeight: 600, marginTop: 6, color: !refCheck.ok || refCheck.repeated ? "var(--wa-oro)" : "var(--wa-acento-texto)" }}>
					{!refCheck.ok || refCheck.repeated ? refCheck.message : "✓ " + R.refOk}
				</div>
			) : ""}
		</div>
	);

	/** Los renglones de una persona por curso, cada grupo con su encabezado (Esteban, 2026-11-02: con dos diplomados la lista era una sola tira). */
	const gruposDe = (p: Person): { k: string; titulo: string; sub: string; rows: Row[] }[] => {
		const orden: string[] = [];
		const por: Record<string, Row[]> = {};
		p.rows.forEach((row) => {
			const k = row.enrollmentId ? "e" + row.enrollmentId : "otros";
			if (!por[k]) { por[k] = []; orden.push(k); }
			por[k].push(row);
		});
		return orden.map((k) => {
			const r0 = por[k][0];
			return k === "otros" ? { k, titulo: R.otherConcepts, sub: "", rows: por[k] }
				: { k, titulo: cursoCorto(r0.courseName) || r0.courseName, sub: r0.scheduleName ? horarioBonito(r0.scheduleName, { plural: true, separador: " " }) : "", rows: por[k] };
		});
	};
	const grupoCabeza = (g: { k: string; titulo: string; sub: string }) => (
		<div style={{ background: "var(--wa-papel)", borderRadius: 6, padding: "6px 10px" }}><b>{g.titulo}</b>{g.sub ? <span style={{ color: "var(--wa-apagado)", fontWeight: 500 }}> {g.sub}</span> : null}</div>
	);
	/** Los renglones que se pintan solos (un recargo con su mensualidad en la lista va dentro de ella, rowCells). */
	const propiosDe = (p: Person, rows: Row[]): Row[] => rows.filter((row) => !(row.parentKey && p.rows.some((x) => x.key === row.parentKey)));
	/**
	 * Los renglones a la vista: los primeros 4 y hasta el último marcado. Lo que se cobra nunca queda escondido: una mensualidad cuenta
	 * como marcada si ella o su recargo lo están (la sugerencia de En Camino marca el recargo aunque no alcance para su mes).
	 */
	const visiblesDe = (p: Person, g: { k: string; rows: Row[] }): Row[] => {
		const propios = propiosDe(p, g.rows);
		if (gruposAbiertos[p.student.id + g.k] || propios.length <= 5) return g.rows;
		const padresMarcados = new Set(g.rows.filter((row) => row.parentKey && p.lines[row.key]?.on).map((row) => row.parentKey as string));
		let ultimo = -1;
		propios.forEach((row, i) => {
			if (p.lines[row.key]?.on || padresMarcados.has(row.key) || (row.surchargeKey && p.lines[row.surchargeKey]?.on)) ultimo = i;
		});
		const vistos = new Set(propios.slice(0, Math.max(4, ultimo + 1)).map((row) => row.key));
		return g.rows.filter((row) => vistos.has(row.key) || (!!row.parentKey && vistos.has(row.parentKey)));
	};
	const verMas = (p: Person, g: { k: string; rows: Row[] }) => {
		const faltan = propiosDe(p, g.rows).length - propiosDe(p, visiblesDe(p, g)).length;
		return faltan > 0 ? (
			<button type="button" className="btn btn-outline-secondary btn-sm rounded-pill" style={{ display: "inline-flex", alignItems: "center", gap: 8, minHeight: movil ? 44 : 32 }}
				onClick={() => setGruposAbiertos((x) => ({ ...x, [p.student.id + g.k]: true }))}>
				<FontAwesomeIcon icon={solid("chevron-down")} />{g.k === "otros" ? R.moreRows(faltan) : R.moreMonths(faltan)}
			</button>
		) : null;
	};

	const rowCells = (p: Person, row: Row): React.ReactNode => {
		if (row.parentKey && p.rows.some((x) => x.key === row.parentKey)) return null;   // va dentro de su mensualidad
		const child = row.surchargeKey ? p.rows.find((x) => x.key === row.surchargeKey) || null : null;
		const box = special && special.studentId === p.student.id && special.key === row.key ? specialBoxFn()
			: waiver && waiver.studentId === p.student.id && (waiver.key === row.key || (child && waiver.key === child.key)) ? waiverBoxFn() : null;
		const main = rowMain(p, row, child);
		if (!box) return main;
		return movil ? <React.Fragment key={row.key}>{main}{box}</React.Fragment>
			: <React.Fragment key={row.key}>{main}<tr><td></td><td colSpan={6} style={{ paddingBottom: 8 }}>{box}</td></tr></React.Fragment>;
	};
	/** Lo del recargo dentro del renglón: "+ recargo 300", días, próximo aumento, eximir/sugerir, sugerencia pendiente. */
	const surchargeInfo = (p: Person, s: Row): React.ReactNode => {
		const info = s.surcharge || {};
		const wr = info.waiverRequest;
		const canAct = !!info.canWaiveNow && !(wr && wr.statusId === DR_PENDIENTE);
		return (
			<div style={{ fontSize: 12, color: "var(--wa-oro)", fontWeight: 600, marginTop: 2 }}>
				{R.surchargeLine(info.days)}{info.waived > 0 ? " · " + R.surchargeWaived(money(info.waived, currencySymbol)) : ""}
				{info.next ? <span style={{ color: "var(--wa-apagado)", fontWeight: 500 }}> · {R.surchargeNext(money(info.next.amount, currencySymbol), fecha(info.next.date))}</span> : null}
				{info.state === "detenido" ? <span style={{ color: "var(--wa-apagado)", fontWeight: 500 }}> · {R.surchargeStopped}</span> : null}
				{wr && wr.statusId === DR_PENDIENTE ? <span> · {R.waiverPending}</span> : null}
				{wr && wr.statusId === DR_RECHAZADO ? <span style={{ fontWeight: 500 }}> · {R.waiverRejected(wr.decisionNotes || "")}</span> : null}
				{canAct ? <> · <button type="button" className="btn btn-link btn-sm p-0" style={{ fontSize: 12, verticalAlign: "baseline" }} disabled={busy}
					onClick={() => setWaiver({ studentId: p.student.id, key: s.key, surchargeId: Number(info.surchargeId), mode: p.canWaive ? "waive" : "request", calcType: "percent", value: "", reason: "", pending: s.pending })}>
					{R.waive}</button></> : null}
			</div>
		);
	};

	const rowMain = (p: Person, row: Row, child?: Row | null) => {
		const l = p.lines[row.key];
		const due = dueOf(p, row, l);
		const s = l.on ? stateOf(p, row, l) : "";
		const options = promosFor(row, p.promos);
		const dr = row.discountRequest;
		const selectValue = l.promotionId ? "p" + l.promotionId : (l.drId ? "d" + l.drId : "");
		const drShown = dr && (dr.statusId === DR_PENDIENTE || dr.statusId === DR_ACEPTADO);
		const apartada = frozenFor(p, row);
		const bloq = lockedFor(p, row) || apartada;
		const enPromo = promoKeyFor(p, row);
		// Renglón de la promo de adelanto: sin descuento especial ni nota de descuento.
		const conEsp = !bloq && !enPromo && !!(l.promotionId || l.drId);
		const descuento = apartada ? <span style={{ fontSize: 12, fontWeight: 600, color: "var(--wa-oro)" }}>{R.ecApartada}</span>
			: bloq || enPromo ? <span style={{ fontSize: 12, fontWeight: 600, color: "var(--wa-acento-texto)" }}>{promoApp!.option.name}</span> : row.canDiscount ? (
			<select className="form-select form-select-sm" value={selectValue} disabled={busy} style={{ textOverflow: "ellipsis" }}
				title={l.promotionId ? (p.promos.find((x) => x.id === l.promotionId)?.conditions || "") : (row.agreementLock ? R.agreementLocked : "")}
				onChange={(e) => {
					const v = e.target.value;
					if (v === "esp") { setSpecial({ studentId: p.student.id, key: row.key, calcType: "percent", value: "", reason: "" }); return; }
					setLine(p.student.id, row.key, (x) => ({ ...x, promotionId: v.startsWith("p") ? Number(v.slice(1)) : null, drId: v.startsWith("d") ? Number(v.slice(1)) : null, touched: false }));
				}}>
				<option value="">{row.agreementLock ? R.agreementLocked : row.currentDiscount > 0 ? "Ya trae " + money(row.currentDiscount, currencySymbol) : R.noDiscount}</option>
				{options.map((o) => <option key={"o" + o.id} value={"p" + o.id}>{row.agreementLock ? "Cambiar a " : ""}{o.name}{o.countdownToIso ? " · " + restante(Date.parse(o.countdownToIso) - (Date.now() + (o.offsetMs || 0))).toLowerCase() : ""}{o.quota?.text ? " · " + o.quota.text : ""}</option>)}
				{drShown ? <option value={"d" + dr!.id}>{dr!.statusId === DR_ACEPTADO ? R.specialAccepted(money(dr!.discountAmount, currencySymbol)) : R.specialPending}</option> : null}
				<option value="esp">{R.special}</option>
			</select>
		) : <span style={{ color: "var(--wa-apagado)", fontSize: row.discountBlocked && row.typeId === 1 ? 12 : undefined }}>{row.discountBlocked && row.typeId === 1 ? R.lateNoDiscount : (row.currentDiscount > 0 ? money(row.currentDiscount, currencySymbol) : "—")}</span>;
		const drNote = bloq || enPromo ? null : dr && l.drId === dr.id && dr.statusId === DR_PENDIENTE ? <div style={{ fontSize: 12, color: "var(--wa-oro)", fontWeight: 600 }}>{R.specialPending} <button type="button" className="btn btn-link btn-sm p-0" style={{ fontSize: 12 }} onClick={() => refreshPerson(p.student.id)}>{R.specialRefresh}</button></div>
			: dr && dr.statusId === DR_RECHAZADO ? <div style={{ fontSize: 12, color: "var(--wa-oro)" }}>{R.specialRejected(dr.decisionNotes || "")}</div> : null;
		const nota = conEsp ? <input className="form-control form-control-sm" value={l.note} disabled={busy} placeholder={R.cols.note} onChange={(e) => setLine(p.student.id, row.key, (x) => ({ ...x, note: e.target.value }))} /> : <span style={{ color: "var(--wa-tenue)" }}>—</span>;
		const seAplica = (
			<input className="form-control form-control-sm" type="number" min="0" step="0.01" style={{ textAlign: "right", fontWeight: 700, maxWidth: 120, marginLeft: "auto" }}
				value={!l.on ? "" : (l.touched && !bloq ? l.apply : String(due))} disabled={busy || bloq || !l.on || due <= 0.009}
				onChange={(e) => setLine(p.student.id, row.key, (x) => ({ ...x, apply: e.target.value, touched: true }))} />
		);
		const concepto = (
			<div>
				<span style={{ fontWeight: 600 }}>{row.title}</span>
				{row.overdue ? pill(R.overdue, "oro") : null}
				{apartada ? pill(R.ecApartada, "oro") : null}
				{row.invoiceId ? pill(R.invoicePill(row.invoiceId), "filo") : null}
				<div style={{ fontSize: 12, color: "var(--wa-apagado)" }}>
					{/* Curso y horario van en el encabezado de su grupo (gruposDe); aquí solo lo que es del renglón. */}
					{[row.enrollmentId ? "" : cursoCorto(row.courseName), row.enrollmentId || !row.scheduleName ? "" : horarioBonito(row.scheduleName, { plural: true, separador: " " }), row.monthIndex && row.monthsTotal ? `Mes ${row.monthIndex} de ${row.monthsTotal}` : "", row.campusId !== campusId ? row.campusName : ""].filter(Boolean).join(" · ")}
				</div>
				{drNote}
				{row.agreement ? (
					<div style={{ fontSize: 12, fontWeight: 600, color: esRegalo(row) ? "var(--wa-oro)" : "var(--wa-acento-texto)" }}>
						{R.agreement(row.agreement.name)} · {esRegalo(row)
							? R.giftLocked(row.agreement.conditionText || "")
							: row.agreement.state === "gratis" || row.agreement.state === "regalo" ? R.giftReady
								: row.agreement.state === "lista" ? R.agreementList
									: R.agreementPrice(money(row.subTotal, currencySymbol), money(row.listAmount, currencySymbol))}
					</div>
				) : row.agreementLock ? <div style={{ fontSize: 12, color: "var(--wa-apagado)" }}>{R.agreement(row.agreementLock)}</div> : null}
				{aTiempo(row) ? <div style={{ fontSize: 12, color: "var(--wa-acento-texto)", fontWeight: 600 }}>{R.onTime(fecha(paymentDate), fecha(row.surcharge.dueDate), money(pendienteATiempo(row), currencySymbol))}</div> : null}
				{row.typeId === 2 && !child ? surchargeInfo(p, row) : null}
				{child && recargoAnulado(p, child) ? (
					<div style={{ fontSize: 12, color: "var(--wa-apagado)", marginTop: 4 }}><span style={{ textDecoration: "line-through" }}>+ {R.surcharge} {money(child.pending, currencySymbol)}</span> · {R.onTimeSurcharge}</div>
				) : child ? (
					<div style={{ display: "flex", gap: 6, alignItems: "flex-start", marginTop: 4 }}>
						<input type="checkbox" checked={!!p.lines[child.key]?.on} disabled={busy} aria-label={child.title}
							onChange={(e) => setLine(p.student.id, child.key, (x) => ({ ...x, on: e.target.checked, touched: false, apply: "" }))} />
						<div><span style={{ fontWeight: 600 }}>+ {R.surcharge}</span>{surchargeInfo(p, child)}</div>
					</div>
				) : null}
			</div>
		);
		const cl = child ? p.lines[child.key] : null;
		const childDue = child && cl ? dueOf(p, child, cl) : 0;
		const childState = child && cl && cl.on ? stateOf(p, child, cl) : "";
		const childMonto = child && !recargoAnulado(p, child) ? <div style={{ color: "var(--wa-oro)", fontWeight: 600 }}>+ {money(childDue, currencySymbol)}</div> : null;
		const childApply = child && cl && !recargoAnulado(p, child) ? (
			<input className="form-control form-control-sm" type="number" min="0" step="0.01" aria-label={R.surcharge}
				style={{ textAlign: "right", fontWeight: 700, maxWidth: 120, marginLeft: "auto", marginTop: 4 }}
				value={!cl.on ? "" : (cl.touched ? cl.apply : String(childDue))} disabled={busy || !cl.on || childDue <= 0.009}
				onChange={(e) => setLine(p.student.id, child.key, (x) => ({ ...x, apply: e.target.value, touched: true }))} />
		) : null;
		const monto = (
			<div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
				{discountOf(p, row, l) >= 0 && l.on ? <div style={{ textDecoration: "line-through", color: "var(--wa-apagado)", fontSize: 12 }}>{money(row.pending, currencySymbol)}</div> : null}
				<div>{money(due, currencySymbol)}</div>
				{childMonto}
			</div>
		);
		const check = <input type="checkbox" checked={l.on} disabled={busy || bloq} aria-label={row.title}
			onChange={(e) => toggleRow(p.student.id, row.key, e.target.checked)} />;
		if (movil) {
			return (
				<div key={row.key} style={{ borderTop: "1px solid var(--wa-filo)", padding: "8px 0", opacity: l.on ? 1 : 0.85 }}>
					<div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>{check}<div className="rpFila" style={{ flex: 1 }} onClick={(e) => {
						// Tocar el renglón marca/desmarca (en el teléfono la casilla sola es un blanco chico); no si se tocó un control.
						if (busy || (e.target as HTMLElement).closest("input,button,select,textarea,a")) return;
						toggleRow(p.student.id, row.key, !l.on);
					}}>{concepto}<div style={{ fontSize: 12, color: "var(--wa-apagado)" }}>{R.cols.due} {fecha(row.dueDate)}</div></div>{monto}</div>
					<div style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: 6, marginTop: 6, paddingLeft: 32 }}>{descuento}<div>{seAplica}{childApply}</div></div>
					{conEsp ? <div style={{ marginTop: 6, paddingLeft: 32 }}>{nota}</div> : null}
					{s || childState ? <div style={{ textAlign: "right", marginTop: 4 }}>{s ? stateTag(s) : null}{childState ? stateTag(childState) : null}</div> : null}
				</div>
			);
		}
		return (
			<tr key={row.key} style={{ opacity: l.on ? 1 : 0.85, borderTop: "1px solid var(--wa-filo)", verticalAlign: "middle" }}>
				<td style={{ width: 28, padding: "10px 0" }}>{check}</td>
				<td style={{ padding: "10px 0" }}>{concepto}</td>
				<td style={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", padding: "0 8px" }}>{fecha(row.dueDate)}</td>
				<td style={{ whiteSpace: "nowrap", paddingRight: 8 }}>{monto}</td>
				<td style={{ width: 200 }}>{descuento}{conEsp ? <div style={{ marginTop: 4 }}>{nota}</div> : null}</td>
				<td style={{ width: 130 }}>{seAplica}{childApply}</td>
				<td style={{ width: 96, textAlign: "right" }}>{s ? stateTag(s) : null}{childState ? <div style={{ marginTop: 8 }}>{stateTag(childState)}</div> : null}</td>
			</tr>
		);
	};

	const specialBoxFn = () => special ? (() => {
		const p = persons.find((x) => x.student.id === special.studentId);
		const row = p?.rows.find((x) => x.key === special.key);
		if (!p || !row) return null;
		const preview2 = Number(special.value) > 0 ? discountFrom(row.listAmount, special.calcType, Number(special.value)) : 0;
		return (
			<div style={{ border: "1px solid var(--wa-oro)", background: "var(--wa-oro-suave)", padding: 10, marginTop: 8 }}>
				<b>{R.specialTitle}</b> · {row.title} · lista {money(row.listAmount, currencySymbol)}
				<div style={{ display: "grid", gridTemplateColumns: movil ? "1fr 1fr" : "110px 120px 1fr auto auto", gap: 8, marginTop: 8, alignItems: "end" }}>
					<select className="form-select form-select-sm" value={special.calcType} onChange={(e) => setSpecial({ ...special, calcType: e.target.value as any })}>
						<option value="percent">{R.specialPercent}</option><option value="amount">{R.specialAmount}</option>
					</select>
					<input className="form-control form-control-sm" type="number" min="0" step="0.01" value={special.value} onChange={(e) => setSpecial({ ...special, value: e.target.value })} />
					<input className="form-control form-control-sm" placeholder={R.specialReason} value={special.reason} onChange={(e) => setSpecial({ ...special, reason: e.target.value })} style={{ gridColumn: movil ? "1 / -1" : undefined }} />
					<button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => setSpecial(null)}>{R.cancel}</button>
					<button type="button" className="btn btn-primary btn-sm" disabled={busy || !(Number(special.value) > 0) || special.reason.trim().length < 10} onClick={sendSpecial}>{R.specialSend}</button>
				</div>
				{preview2 > 0 ? <div style={{ fontSize: 12, marginTop: 4 }}>− {money(preview2, currencySymbol)} → {money(row.listAmount - preview2, currencySymbol)}</div> : null}
			</div>
		);
	})() : null;

	const waiverBoxFn = () => waiver ? (() => {
		const amount = Number(waiver.value) > 0 ? (waiver.calcType === "percent" ? round2(waiver.pending * Math.min(100, Number(waiver.value)) / 100) : Math.min(waiver.pending, round2(Number(waiver.value)))) : 0;
		return (
			<div style={{ border: "1px solid var(--wa-oro)", background: "var(--wa-oro-suave)", padding: 10, marginTop: 8 }}>
				<b>{R.waiveTitle}</b> · {R.surchargeOwes(money(waiver.pending, currencySymbol))}
				<div style={{ display: "grid", gridTemplateColumns: movil ? "1fr 1fr" : "110px 120px 1fr auto auto", gap: 8, marginTop: 8, alignItems: "end" }}>
					<select className="form-select form-select-sm" value={waiver.calcType} onChange={(e) => setWaiver({ ...waiver, calcType: e.target.value as any })}>
						<option value="percent">{R.specialPercent}</option><option value="amount">{R.specialAmount}</option>
					</select>
					<input className="form-control form-control-sm" type="number" min="0" step="0.01" value={waiver.value} onChange={(e) => setWaiver({ ...waiver, value: e.target.value })} />
					<input className="form-control form-control-sm" placeholder={R.waiveReason} value={waiver.reason} onChange={(e) => setWaiver({ ...waiver, reason: e.target.value })} style={{ gridColumn: movil ? "1 / -1" : undefined }} />
					<button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => setWaiver(null)}>{R.cancel}</button>
					<button type="button" className="btn btn-primary btn-sm" disabled={busy || !(amount > 0) || waiver.reason.trim().length < 10} onClick={waiver.mode === "waive" ? sendWaiver : pedirEximirRecargo}>{waiver.mode === "waive" ? R.waiveSend : R.sendApproval}</button>
				</div>
				{amount > 0 ? <div style={{ fontSize: 12, marginTop: 4 }}>− {money(amount, currencySymbol)} → {money(Math.max(0, waiver.pending - amount), currencySymbol)}{amount >= waiver.pending - 0.009 ? " · " + R.waiveTotal : ""}</div> : null}
			</div>
		);
	})() : null;

	const blockAplica = (
		<div style={{ marginTop: 18 }}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
				<h3 style={{ fontSize: 16, margin: 0 }}>{R.applyBlock} <small style={{ fontWeight: 500, color: "var(--wa-apagado)", fontSize: 13 }}>{R.applyBlockHint}</small></h3>
				{manual ? <button type="button" className="btn btn-link btn-sm p-0" disabled={busy} onClick={() => { setManual(false); setPersons((prev) => suggest(prev, received)); }}>{R.suggestAgain}</button> : null}
			</div>
			{persons.map((p) => (
				<div key={"p" + p.student.id} style={{ border: "1px solid var(--wa-filo)", marginTop: 10 }}>
					<div style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "8px 12px", background: "var(--wa-papel)", flexWrap: "wrap" }}>
						<div><b style={{ fontSize: 15 }}>{nameOf(p.student)}</b> <span style={{ color: "var(--wa-apagado)", fontSize: 13 }}>· {p.student.identityCode || ""}{p.campusNames.length ? " · " + p.campusNames.join(", ") : ""}</span></div>
						<div style={{ display: "flex", gap: 14, alignItems: "center", marginLeft: "auto" }}>
							{/* Cuánto de este pago va a esta persona (Esteban, 2026-10-27: "de esta cantidad cuánto va a cada quien"). */}
							{persons.length > 1 ? <span style={{ fontSize: 14 }}>{R.personApplied} <b>{money(round2(p.rows.reduce((s2, row) => s2 + (p.lines[row.key]?.on ? applyOf(p, row, p.lines[row.key]) : 0), 0)), currencySymbol)}</b></span> : null}
							{/* «Aplicar Promoción» vive aquí, junto al nombre (ESPEC_SOLICITUD_PROMO front 6); el panel y el aviso «cuadra» van abajo. */}
							{promoHas[p.student.id] !== false && promoOpen !== p.student.id && !promoApp
								? <button type="button" className="btn btn-outline-secondary btn-sm rounded-pill" style={{ minHeight: movil ? 44 : 34, display: "inline-flex", alignItems: "center", gap: 8 }} disabled={busy} onClick={() => setPromoOpen(p.student.id)}><FontAwesomeIcon icon={solid("tag")} />{R.promoApply}</button> : null}
							{ticket && p.student.id === Number(ticket.studentId) ? null : <button type="button" className="btn btn-link btn-sm p-0" disabled={busy} onClick={() => removePerson(p.student.id)}>{R.remove}</button>}
						</div>
					</div>
					<div style={{ padding: "0 12px 8px" }}>
						{(ecVivas[p.student.id] || []).map((e) => <BandaEnCamino key={"ec" + e.id} e={e} sym={currencySymbol} />)}
						{/* Limpieza sin hacer (Esteban, 2026-11-02): su mensualidad no se cobra hasta decidir la promoción; se revisa aquí mismo. */}
						{(p.cleanupPending || []).map((c: any) => (
							<div key={"lp" + c.cleanupId} style={{ marginTop: 8, padding: "8px 12px", borderRadius: 8, background: "var(--wa-oro-suave)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
								<span style={{ fontSize: 14 }}><b>{c.courseName || "Su curso"}: promoción por revisar.</b> Su mensualidad no se cobra hasta confirmarla (o «Sin Promoción»).</span>
								<button type="button" className="btn btn-primary btn-sm" style={{ borderRadius: 999, minHeight: movil ? 44 : undefined }} disabled={busy} onClick={() => setRevisandoLimp(c)}>Revisar Promoción</button>
							</div>
						))}
						{p.warnings.map((w, i) => <div key={"w" + i} style={{ color: "var(--wa-oro)", fontSize: 13, fontWeight: 600, paddingTop: 6 }}>{w}</div>)}
						<AplicarPromocion campusId={campusId} student={p.student} sym={currencySymbol} amount={received} paymentDate={paymentDate} intentId={intentId}
							open={promoOpen === p.student.id} movil={movil} busy={busy} serverError={serverError}
							preload={!promoQuitada && ((!!ticket && (draft?.promo ? Number(draft.promo.studentId) === p.student.id : (!!ticket.requestId || esSolicitudPromo) && p.student.id === Number(ticket.studentId))) || promoPreload?.studentId === p.student.id)}
							restore={promoPreload?.studentId === p.student.id ? promoPreload.draft : draft?.promo && Number(draft.promo.studentId) === p.student.id ? draft.promo : null}
							preloadKey={promoPreload?.studentId === p.student.id ? promoPreload.key : ""}
							onForeignDraft={(d) => abrirEnQuienPaga(d)}
							selected={promoApp && promoApp.studentId === p.student.id ? promoApp : null} seCobra={applied} recargosATiempo={(o) => recargosATiempo(p, o)} otherSelected={!!promoApp && promoApp.studentId !== p.student.id}
							onOpen={() => setPromoOpen(p.student.id)} onClose={() => setPromoOpen(null)}
							onSelect={(o, d, ec) => elegirPromo(p.student.id, o, d, ec || null)}
							enCaminoOk={!promoQuitada} recibido={promoApp?.enCamino && promoApp.studentId === p.student.id ? ecParaPromo() : received}
							onEnCamino={(list) => setEcVivas((prev) => (JSON.stringify(prev[p.student.id] || []) === JSON.stringify(list) ? prev : { ...prev, [p.student.id]: list }))}
							onRefresh={(o) => setPromoApp((cur) => cur && cur.studentId === p.student.id && cur.option.enrollmentId === o.enrollmentId && cur.option.promotionId === o.promotionId
								&& JSON.stringify(cur.option) !== JSON.stringify(o) ? { ...cur, option: o, giftOptionKey: initialGiftKey(o, cur.giftOptionKey) } : cur)}
							onClear={quitarPromo}
							onApplied={(o, res) => { setPromoApp(null); setPromoQuitada(true);
								setNotice("Promoción Aplicada Sin Cobrar: " + (res?.promoName || o.name) + (res && res.giftValue > 0.009 ? " (Regalo " + money(res.giftValue, currencySymbol) + ")" : "")); refreshPerson(p.student.id); }}
							onChange={(patch) => setPromoApp((cur) => cur ? { ...cur, ...patch } : cur)}
							showButton={false} onHasOptions={(has) => setPromoHas((prev) => (prev[p.student.id] === has ? prev : { ...prev, [p.student.id]: has }))} />
						{cambios.filter((c) => c.studentId === p.student.id).map((c) => (
							<div key={"cp" + c.k} style={{ border: "1px solid var(--wa-oro)", background: "var(--wa-oro-suave)", padding: "8px 10px", marginTop: 8, fontSize: 13 }}>
								<b>Cambio de promoción → «{c.promoName}»</b>
								<div style={{ margin: "2px 0 4px" }}>{c.blocked || c.text}</div>
								{!c.blocked && c.months.length ? (
									<ul style={{ margin: "0 0 6px", paddingLeft: 18 }}>
										{c.months.map((mes) => <li key={"cm" + mes.key}>{mes.title}: {money(mes.subTotal, currencySymbol)} → {money(mes.list, currencySymbol)} · se suman {money(mes.difference, currencySymbol)}</li>)}
									</ul>
								) : null}
								{!c.blocked ? (confirmados[c.k] ? (
									<div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
										<span style={{ fontWeight: 700 }}>✓ Confirmado: se suman {money(extraDeCambio(c), currencySymbol)} a este cobro. Lo pagado no se anula; va a Auditoría.</span>
										<button type="button" className="btn btn-link btn-sm p-0" disabled={busy} onClick={() => setConfirmados((x) => ({ ...x, [c.k]: false }))}>Deshacer</button>
									</div>
								) : (
									<button type="button" className="btn btn-primary btn-sm rounded-pill" style={{ minHeight: 40 }} disabled={busy}
										onClick={() => setConfirmados((x) => ({ ...x, [c.k]: true }))}>Cambiar la Promoción{c.months.length ? " y Sumar " + money(c.difference, currencySymbol) : ""}</button>
								)) : null}
							</div>
						))}
						{(p.blocked || []).filter((b) => b.canApprove).map((b) => (
							<div key={"bl" + b.agreementId} style={{ border: "1px solid var(--wa-oro)", padding: "8px 10px", marginTop: 8, fontSize: 13 }}>
								{excepcion && excepcion.agreementId === b.agreementId ? (
									<>
										<b>{R.exceptionTitle(b.name)}</b>
										<div style={{ color: "var(--wa-apagado)", margin: "2px 0 6px" }}>{R.exceptionHelp}</div>
										<textarea className="form-control" rows={2} style={{ fontSize: 16 }} value={excepcion.reason} disabled={excBusy} placeholder={R.exceptionReason}
											onChange={(e) => setExcepcion({ agreementId: b.agreementId, reason: e.target.value })} />
										<div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
											<button type="button" className="btn btn-primary btn-sm rounded-pill" style={{ minHeight: 40 }} disabled={excBusy || excepcion.reason.trim().length < 10}
												onClick={() => aprobarExcepcion(p.student, b.agreementId)}>{excBusy ? "Enviando…" : R.exceptionConfirm}</button>
											<button type="button" className="btn btn-link btn-sm" style={{ minHeight: 40 }} disabled={excBusy} onClick={() => setExcepcion(null)}>{R.exceptionCancel}</button>
										</div>
									</>
								) : (
									<button type="button" className="btn btn-outline-primary btn-sm rounded-pill" style={{ minHeight: 40 }} disabled={busy}
										onClick={() => setExcepcion({ agreementId: b.agreementId, reason: "" })}>{R.exceptionButton(b.name)}</button>
								)}
							</div>
						))}
						{(p.advances || []).map((adv) => {
							const faltan = adv.keys.filter((k) => !(p.lines[k] && p.lines[k].on && stateOf(p, p.rows.find((x) => x.key === k) as Row, p.lines[k]) === "full")).length;
							const gifts = adv.gifts.filter((g) => g.statusId !== 2);
							const giftText = adv.giftStudentId !== p.student.id ? R.advanceGiftOther(persons.find((x) => x.student.id === adv.giftStudentId) ? nameOf(persons.find((x) => x.student.id === adv.giftStudentId)!.student) : "otra persona")
								: gifts.map((g) => g.title).join(", ");
							return (
								<div key={"adv" + adv.agreementId} style={{ border: "1px dashed var(--wa-oro)", background: "var(--wa-oro-suave)", padding: "8px 10px", marginTop: 8, fontSize: 13 }}>
									<b>{R.advanceTitle(adv.name)}</b> · {R.advanceText(money(adv.amount, currencySymbol), adv.pendingCount)} {gifts.length || adv.giftStudentId !== p.student.id ? (adv.adelanto ? R.advanceGiftsApply(giftText) : R.advanceGifts(giftText)) : ""}
									<div style={{ color: "var(--wa-apagado)", marginTop: 2 }}>{R.advanceNormal}{adv.text ? " " + adv.text : ""}</div>
									{adv.adelanto ? (
										promoApp && promoApp.studentId === p.student.id && promoApp.option.agreementId === adv.agreementId ? null
											: <button type="button" className="btn btn-outline-primary btn-sm rounded-pill" style={{ marginTop: 6, minHeight: 40 }} disabled={busy || (!!promoApp && promoApp.studentId !== p.student.id)}
												onClick={() => aplicarAdelanto(p.student.id, adv)}>{R.advanceApply}</button>
									) : faltan > 0 && adv.keys.length > 0 ? <button type="button" className="btn btn-outline-primary btn-sm rounded-pill" style={{ marginTop: 6 }} disabled={busy} onClick={() => marcarAdelanto(p.student.id, adv)}>{R.advanceMark}</button> : null}
								</div>
							);
						})}
						{p.loading ? <div style={{ color: "var(--wa-apagado)", padding: 8 }}>{R.loading}</div>
							: p.rows.length < 1 ? <div style={{ color: "var(--wa-oro)", padding: 8, fontWeight: 600 }}>{R.nothing}</div>
								: movil ? <div>{gruposDe(p).map((g) => (
									<React.Fragment key={"g" + g.k}>
										<div style={{ padding: "12px 0 6px", fontSize: 14 }}>{grupoCabeza(g)}</div>
										{visiblesDe(p, g).map((row) => rowCells(p, row))}
										{verMas(p, g) ? <div style={{ padding: "8px 0" }}>{verMas(p, g)}</div> : null}
									</React.Fragment>
								))}</div>
									: (
										<table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }} className="waRegistrarTabla">
											<thead><tr style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--wa-apagado)" }}>
												<th></th><th>{R.cols.concept}</th><th>{R.cols.due}</th><th style={{ textAlign: "right" }}>{R.cols.amount}</th><th style={{ paddingLeft: 8 }}>{R.cols.discount}</th><th style={{ textAlign: "right" }}>{R.cols.apply}</th><th></th>
											</tr></thead>
											<tbody>{gruposDe(p).map((g) => (
												<React.Fragment key={"g" + g.k}>
													<tr><td colSpan={7} style={{ padding: "14px 0 6px", fontSize: 14 }}>{grupoCabeza(g)}</td></tr>
													{visiblesDe(p, g).map((row) => rowCells(p, row))}
													{verMas(p, g) ? <tr><td></td><td colSpan={6} style={{ padding: "8px 0 10px" }}>{verMas(p, g)}</td></tr> : null}
												</React.Fragment>
											))}</tbody>
										</table>
									)}
					</div>
				</div>
			))}
			<div style={{ marginTop: 10 }}>
				{!searchOpen ? <button type="button" className="btn btn-outline-primary btn-sm rounded-pill" disabled={busy} onClick={() => setSearchOpen(true)}>{R.addPerson}</button> : (
					<div style={{ position: "relative", maxWidth: 520 }}>
						<input className="form-control" autoFocus value={query} placeholder={R.searchPlaceholder} onChange={(e) => search(e.target.value)} />
						{query.trim().length >= 2 ? (
							<div style={{ background: "var(--wa-superficie)", border: "1px solid var(--wa-filo)", borderTop: 0, maxHeight: 260, overflow: "auto" }}>
								{searching ? <div style={{ padding: 8, color: "var(--wa-apagado)" }}>…</div>
									: results.length < 1 ? <div style={{ padding: 8, color: "var(--wa-apagado)" }}>{R.noResults}</div>
										: results.map((s: any) => {
											const dup = persons.some((p) => p.student.id === s.id);
											return (
												<div key={"r" + s.id} style={{ padding: "6px 10px", cursor: dup ? "default" : "pointer", opacity: dup ? 0.5 : 1, borderBottom: "1px solid var(--wa-filo)" }}
													onClick={() => !dup && addPerson({ id: s.id, name: s.name, identityCode: s.identityCode })}>
													<b>{s.name}</b> <span style={{ color: "var(--wa-apagado)", fontSize: 12 }}>· {s.identityCode || ""} · {s.campusName}{dup ? " · " + R.alreadyIn : ""}</span>
												</div>
											);
										})}
							</div>
						) : null}
					</div>
				)}
			</div>
		</div>
	);

	const comprobante = ticket ? (
		<aside style={{ background: "var(--wa-papel)", border: "1px solid var(--wa-filo)", padding: 12, alignSelf: "start" }}>
			<div style={{ fontSize: 11, letterSpacing: ".08em", textTransform: "uppercase", fontWeight: 700, color: "var(--wa-apagado)" }}>{R.files}</div>
			{ticketDocs.length > 1 ? (
				<div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "6px 0" }}>
					{ticketDocs.map((d: any) => <button key={"d" + d.index} type="button" className={"btn btn-sm " + (preview?.index === d.index ? "btn-primary" : "btn-outline-secondary")} onClick={() => showDoc(d.index)}>{d.name}</button>)}
				</div>
			) : null}
			{preview && preview.url ? (
				<div style={{ marginTop: 6, cursor: "zoom-in" }} onClick={() => abrirArchivo({ nombre: preview.name, cargar: () => http.paymentIntentsService.downloadDocument(campusId, Number(ticket.id), preview.index).then((r: any) => r.data) })}>
					{preview.type.startsWith("image/") ? <img src={preview.url} alt={preview.name} style={{ width: "100%", maxHeight: movil ? 260 : 520, objectFit: "contain", background: "var(--wa-superficie)" }} />
						: preview.type === "application/pdf" && !movil ? <iframe title={preview.name} src={preview.url} style={{ width: "100%", height: 520, border: 0 }} />
							: <div style={{ padding: 12 }}>{preview.name}</div>}
				</div>
			) : <div style={{ color: "var(--wa-apagado)", padding: 8 }}>{ticketDocs.length ? "…" : "—"}</div>}
			<div style={{ fontSize: 13, marginTop: 10, display: "grid", gap: 4 }}>
				{ai && ai.loading ? <div style={{ color: "var(--wa-apagado)" }}>{R.aiReading}</div> : null}
				{ai && !ai.loading && ai.readings.length > 0 ? (
					<div style={{ borderBottom: "1px dashed var(--wa-filo)", paddingBottom: 6 }}>
						<span style={{ fontSize: 11, letterSpacing: ".08em", textTransform: "uppercase", fontWeight: 700, color: "var(--wa-apagado)" }}>{R.aiRead}</span>
						{ai.readings.map((x: any, k: number) => <div key={"ar" + k}>{x.summary}</div>)}
					</div>
				) : null}
				{ticket.amount ? <div><span style={{ color: "var(--wa-apagado)" }}>Monto Reportado </span><b>{money(ticket.amount, currencySymbol)}</b></div> : null}
				{ticket.reference ? <div><span style={{ color: "var(--wa-apagado)" }}>Referencia </span><b>{ticket.reference}</b></div> : null}
				{ticket.paidAt ? <div><span style={{ color: "var(--wa-apagado)" }}>Fecha </span><b>{fecha(ticket.paidAt)}</b></div> : null}
				{ticket.notes ? <div style={{ whiteSpace: "pre-wrap" }}><span style={{ color: "var(--wa-apagado)" }}>Nota </span>{String(ticket.notes).split("\n")[0]}</div> : null}
			</div>
			{ticketSinAlumno && payPersons.length > 1 ? (
				<label style={{ ...LB, marginTop: 10 }}>{lab(R.owner)}
					<select className="form-select form-select-sm" value={ownerId} disabled={busy} onChange={(e) => setOwner(Number(e.target.value))}>
						{payPersons.map((p) => <option key={"o" + p.student.id} value={p.student.id}>{nameOf(p.student)}</option>)}
					</select>
				</label>
			) : null}
		</aside>
	) : null;

	const resultView = result ? (
		<div style={{ border: "1px solid var(--wa-acento)", background: "var(--wa-acento-suave)", padding: 14 }}>
			<h3 style={{ fontSize: 18, margin: "0 0 8px" }}>{R.done(result.id)}</h3>
			{(result.movements || []).map((m: any) => (
				<div key={"m" + m.id} style={{ padding: "4px 0" }}>
					<b>{m.label}</b> · {m.studentName}: {(m.concepts || []).map((c: any) => `${c.title} ${R.states2[c.state] || ""}${c.state === "abono" ? " " + money(c.applied, currencySymbol) : ""}`).join(" · ")}
					<span style={{ color: "var(--wa-apagado)" }}> · factura {m.invoiceId}</span>
				</div>
			))}
			{(result.promoApplications || []).length ? <div style={{ color: "var(--wa-acento-texto)", fontWeight: 600, marginTop: 6 }}>Promoción Aplicada{promoApp ? ": " + promoApp.option.name : ""}</div> : null}
			{(result.promosSolas || []).map((t: string, k: number) => <div key={"ps" + k} style={{ color: "var(--wa-acento-texto)", fontWeight: 600, marginTop: 6 }}>{t}</div>)}
			{result.enCamino?.text ? <div style={{ color: "var(--wa-acento-texto)", fontWeight: 600, marginTop: 6 }}>{result.enCamino.text}</div> : null}
			{(result.fulfilled || []).map((f: any) => <div key={"f" + f.agreementId} style={{ color: "var(--wa-acento-texto)", fontWeight: 600, marginTop: 6 }}>{R.fulfilled(f.name, f.saldados)}</div>)}
			{(result.audits || []).length ? <div style={{ color: "var(--wa-oro)", fontWeight: 600, marginTop: 6 }}>{result.audits.map((a: any) => a.details).join(" ")} · Auditoría</div> : null}
			{result.ticketPending > 0 ? <div style={{ color: "var(--wa-oro)", fontWeight: 600, marginTop: 6 }}>{R.ticketStillOpen(result.ticketPending)}</div> : null}
			<button type="button" className="btn btn-outline-primary btn-sm rounded-pill" style={{ marginTop: 10 }} onClick={() => openReceipt(result.id)}>{R.receipt}</button>
		</div>
	) : null;

	return (
		<>
		<Modal className={"waModulo" + (movil ? " rpMovil" : "")} show onHide={busy ? undefined : finish} keyboard={!revisandoLimp} size="xl" centered scrollable fullscreen="lg-down" enforceFocus={false}>
			{/* Teléfono (Esteban, 2026-10-09): casillas y botones del tamaño de un dedo (44 px), no de un cursor. */}
			{movil ? <style>{".rpMovil input[type=checkbox]{width:24px;height:24px;flex:0 0 24px;margin:0}"
				+ ".rpMovil .modal-footer .btn,.rpMovil .modal-body .btn-sm{min-height:44px;padding-left:16px;padding-right:16px}"
				+ ".rpMovil .rpFila{cursor:pointer;min-height:44px}"}</style> : null}
			<Modal.Header closeButton={!busy}>
				<Modal.Title style={{ fontSize: 18 }}>
					{ticket ? R.ticket(Number(ticket.id)) + " · " : ""}{R.title}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body style={{ textAlign: "left", fontSize: 14 }}>
				{error ? <div className="alert alert-danger" style={{ fontSize: 14 }}>{error}</div> : null}
				{preguntaPromo && !result ? (
					<div style={{ border: "2px solid var(--wa-oro)", background: "var(--wa-oro-suave)", padding: "10px 12px", marginBottom: 12 }}>
						<b style={{ color: "var(--wa-oro)" }}>¿Lleva Promoción?</b>
						<div style={{ marginTop: 4 }}>{preguntaPromo}</div>
						{sinPromo === null ? (
							<div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
								<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={busy}
									onClick={() => { setPreguntaPromo(null); if (payPersons[0]) setPromoOpen(payPersons[0].student.id); }}>Aplicar Promoción</button>
								<button type="button" className="btn btn-outline-secondary btn-sm rounded-pill" disabled={busy} onClick={() => setSinPromo("")}>No Lleva Promoción</button>
							</div>
						) : (
							<div style={{ marginTop: 8 }}>
								<label style={{ display: "block", fontWeight: 600 }}>¿Por qué no lleva?
									<input className="form-control form-control-sm" value={sinPromo} disabled={busy} maxLength={300} placeholder="Ej.: paga mes a mes, la promo es de otra persona…"
										onChange={(e) => setSinPromo(e.target.value)} /></label>
								<div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
									<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={busy || sinPromo.trim().length < 10}
										onClick={() => unaVez(() => submitAhora(sinPromo.trim()))}>{busy ? "…" : "Guardar Sin Promoción"}</button>
									<button type="button" className="btn btn-outline-secondary btn-sm rounded-pill" disabled={busy} onClick={() => setSinPromo(null)}>Volver</button>
								</div>
							</div>
						)}
					</div>
				) : null}
				{notice && !result ? <div className="alert alert-warning" style={{ fontSize: 14 }}>{notice}</div> : null}
				{!result && ai && ai.warnings.length > 0 ? (
					<div style={{ border: "1px solid var(--wa-oro)", background: "var(--wa-oro-suave)", padding: "8px 12px", marginBottom: 12 }}>
						<b style={{ color: "var(--wa-oro)" }}>{R.review}</b>
						{ai.warnings.map((w, k) => <div key={"aw" + k} style={{ fontSize: 14 }}>{w}</div>)}
					</div>
				) : null}
				{!result && adjuntos && adjuntos.duplicates.length > 0 ? (
					<div className="alert alert-danger" style={{ fontSize: 14 }}>{adjuntos.duplicates.map((w, k) => <div key={"ad" + k}>{w}</div>)}</div>
				) : null}
				{!result && adjuntos && (adjuntos.loading || adjuntos.warnings.length > 0 || adjuntos.readings.length > 0) ? (
					<div style={{ border: "1px solid var(--wa-oro)", background: "var(--wa-oro-suave)", padding: "8px 12px", marginBottom: 12 }}>
						<b style={{ color: "var(--wa-oro)" }}>{adjuntos.loading ? R.aiReading : R.review}</b>
						{adjuntos.readings.map((x: any, k: number) => <div key={"adr" + k} style={{ fontSize: 14, color: "var(--wa-apagado)" }}>{x.summary}</div>)}
						{adjuntos.warnings.map((w, k) => <div key={"adw" + k} style={{ fontSize: 14 }}>{w}</div>)}
					</div>
				) : null}
				{result ? resultView : (
					<div style={{ display: "grid", gridTemplateColumns: ticket && !movil ? "minmax(0, 0.8fr) minmax(0, 2.2fr)" : "1fr", gap: 16 }}>
						{comprobante}
						<div>{blockPago}{blockAplica}</div>
					</div>
				)}
			</Modal.Body>
			<Modal.Footer style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
				{result ? <div /> : (
					<div style={{ display: "flex", gap: 18, flexWrap: "wrap", fontVariantNumeric: "tabular-nums" }}>
						<span>{R.totals.received} <b style={{ fontSize: 17 }}>{money(received, currencySymbol)}</b></span>
						<span>{R.totals.applied} <b style={{ fontSize: 17 }}>{money(applied, currencySymbol)}</b></span>
						<span style={{ color: Math.abs(left) >= 0.005 ? "var(--wa-oro)" : undefined }}>{R.totals.left} <b style={{ fontSize: 17 }}>{left < 0 ? money(-left, currencySymbol) + " " + R.over : money(left, currencySymbol)}</b></span>
					</div>
				)}
				<div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
					{!result && whyNot && onLines.length > 0 ? <span style={{ fontSize: 12, color: "var(--wa-oro)", fontWeight: 600 }}>{whyNot}</span> : null}
					{result ? <button type="button" className="btn btn-primary btn-sm rounded-pill" onClick={finish}>{result.ticketPending > 0 ? R.nextDeposit : R.close}</button> : (
						<>
							<button type="button" className="btn btn-outline-secondary btn-sm rounded-pill" disabled={busy} onClick={onClose}>{R.cancel}</button>
							{intentId ? <button type="button" className="btn btn-outline-primary btn-sm rounded-pill" disabled={busy} onClick={() => saveDraft(false)}>{R.saveDraft}</button> : null}
							{/* Grey button: hovering it says what is missing (Esteban, 2026-11-02). A disabled button gets no mouse events, so the span listens. */}
							{whyNot && !limpCaso && !busy ? (
								<OverlayTrigger placement="top" overlay={<Tooltip id="rpPorQue">{whyNot}</Tooltip>}>
									<span className="d-inline-block" tabIndex={0}>
										<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled style={{ pointerEvents: "none" }}>{R.register}</button>
									</span>
								</OverlayTrigger>
							) : <button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={busy} onClick={submit}>{busy ? "…" : R.register}</button>}
						</>
					)}
				</div>
			</Modal.Footer>
			{revisandoLimp ? (
				<React.Suspense fallback={null}>
					<LimpiezaDelAlumno campusId={revisandoLimp.campusId} studentId={revisandoLimp.studentId} enrollmentId={revisandoLimp.enrollmentId} nombre={revisandoLimp.name}
						onClose={() => { const sid = revisandoLimp.studentId; setRevisandoLimp(null); refreshPerson(sid); }} />
				</React.Suspense>
			) : null}
		</Modal>
		{aprobacion.modal}
		</>
	);
};

export default RegisterPaymentModal;
